import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from "@/context/AuthContext";
import { useAIService } from "@/context/AIServiceContext";
import { toast } from 'react-toastify';
import { IoChevronDown, IoChevronForward, IoEye, IoEyeOff, IoAdd, IoTrash, IoSave, IoFlash, IoCloudDownload, IoCloudUpload, IoWarning, IoOpenOutline } from 'react-icons/io5';
import '@/styles/SecretsStyles.scss';
import { getIntegrationConfig, shouldShowField, getFieldValue } from '@/config/integrationConfigs';
import IntegrationFieldRenderer from './components/IntegrationFieldRenderer';
import IntegrationSelector from './components/IntegrationSelector';
import * as Sentry from '@sentry/react';

/**
 * SecretsPage - Modern secrets management interface
 * 
 * Features:
 * - Category-based organization
 * - Type-specific forms with conditional fields
 * - Password visibility toggle
 * - Improved UX with collapsible sections
 */
/**
 * Secret keys that are managed by dedicated settings pages and should not be
 * editable from the raw Secrets UI. Each entry maps a secretKey to the tab ID
 * of the dedicated settings page and a human-readable label.
 */
const MANAGED_SECRET_REDIRECTS = {
    FileStorageConfig: {
        tabId: 'fileStorageConfig',
        label: 'File Storage Configuration',
        description: 'Connection, folder structure, and file restriction settings are managed from the dedicated File Storage Config page under System Settings.',
        icon: '📁'
    }
};

const SecretsPage = ({ onNavigateToTab }) => {
    const { apiFetch } = useAuth();
    const { refreshConfig } = useAIService();
    const [secretsByCategory, setSecretsByCategory] = useState({});
    const [loading, setLoading] = useState(true);
    const [, setSelectedSecret] = useState(null);
    const [editSecret, setEditSecret] = useState(null);
    const [isCreateMode, setIsCreateMode] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [secretTypes, setSecretTypes] = useState([]);
    const [categories, setCategories] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [expandedCategories, setExpandedCategories] = useState({});
    const [visiblePasswords, setVisiblePasswords] = useState({});
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [exportIncludePasswords, setExportIncludePasswords] = useState(false);
    const [importOverwriteExisting, setImportOverwriteExisting] = useState(false);
    const [exportingSecrets, setExportingSecrets] = useState(false);
    const [importingSecrets, setImportingSecrets] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [showIntegrationSelector, setShowIntegrationSelector] = useState(false);
    const [monitoringStatuses, setMonitoringStatuses] = useState({});
    const [dbEnvironment, setDbEnvironment] = useState('');
    const [exportingMySql, setExportingMySql] = useState(false);
    const [exportingInflux, setExportingInflux] = useState(false);
    const [showDbImportModal, setShowDbImportModal] = useState(null); // 'mysql' | 'influx' | null
    const [dbImportFile, setDbImportFile] = useState(null);
    const [dbImportConfirmText, setDbImportConfirmText] = useState('');
    const [importingDb, setImportingDb] = useState(false);
    const [dbStatus, setDbStatus] = useState({ mysql: null, influx: null }); // raw server status, keyed by target
    // Ticks every second (while an operation is running) to force elapsed-time recalculation.
    // Starts at 0 (a pure initial value) rather than Date.now() - getDbElapsedSeconds() already
    // returns 0 whenever nothing is running, and the ticking effect below sets a real timestamp
    // within a second of anything actually starting, so this is never read before then.
    const [nowTick, setNowTick] = useState(0);
    // Guards against triggering the same completed-export download more than once across
    // multiple 3s poll ticks (the server only clears isReadyForDownload once our own claim
    // request completes, so there's a brief window where several polls could all see it as
    // still "ready"). Reset whenever a fresh export is started for that target.
    const exportDownloadClaimedRef = useRef({ mysql: false, influx: false });

    /**
     * Fetch secrets grouped by category from the API
     */
    const fetchSecrets = async () => {
        try {
            const response = await apiFetch('/api/AppSecrets/by-category');
            const data = await response.json();
            if (data.success) {
                setSecretsByCategory(data.data);
                // Auto-expand categories that have secrets
                const expanded = {};
                Object.keys(data.data).forEach(category => {
                    expanded[category] = true; // Default to expanded
                });
                setExpandedCategories(expanded);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error(e);
            toast.error('Failed to load secrets');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Fetch available secret types from the API
     */
    const fetchSecretTypes = async () => {
        try {
            const response = await apiFetch('/api/AppSecrets/types');
            const data = await response.json();
            if (data.success) {
                setSecretTypes(data.data);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error(e);
        }
    };

    /**
     * Fetch available categories from the API
     */
    const fetchCategories = async () => {
        try {
            const response = await apiFetch('/api/AppSecrets/categories');
            const data = await response.json();
            if (data.success) {
                setCategories(data.data);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error(e);
        }
    };

    /**
     * Fetch departments for expiry reminder assignment
     */
    const fetchDepartments = async () => {
        try {
            const response = await apiFetch('/api/Department');
            const data = await response.json();
            if (data.success) {
                setDepartments(data.data || []);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error(e);
        }
    };

    const fetchMonitoringStatus = async () => {
        try {
            const response = await apiFetch('/api/AppSecrets/monitoring-status');
            const data = await response.json();
            if (data.success && Array.isArray(data.data)) {
                // Build a lookup map keyed by secretKey for O(1) access in the card render
                const map = {};
                data.data.forEach(s => { map[s.secretKey] = s; });
                setMonitoringStatuses(map);
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            // Non-critical — cards fall back to dot-only rendering
        }
    };

    useEffect(() => {
        const loadInitialData = async () => {
            await Promise.all([
                fetchSecrets(),
                fetchSecretTypes(),
                fetchCategories(),
                fetchDepartments(),
                fetchMonitoringStatus()
            ]);
        };
        loadInitialData();
    }, []);

    useEffect(() => {
        const fetchEnvironment = async () => {
            try {
                const response = await apiFetch('/api/DatabaseBackup/environment');
                const result = await response.json();
                if (result.success) setDbEnvironment(result.data);
            } catch (e) {
                Sentry.captureException(e);
            }
        };
        fetchEnvironment();
    }, [apiFetch]);

    // Poll server-side status so navigating away and back always reflects reality —
    // including the true elapsed time, since startedAtUtc comes from the server, not local state.
    useEffect(() => {
        const pollStatus = async () => {
            try {
                const response = await apiFetch('/api/DatabaseBackup/status');
                const result = await response.json();
                if (result.success) {
                    setDbStatus(result.data); // { mysql: {isRunning, operationType, startedAtUtc, ...}, influx: {...} }

                    // Keep the destructive-import guard flag in sync with server truth too
                    const mysqlImporting = result.data?.mysql?.isRunning && result.data.mysql.operationType === 'Import';
                    const influxImporting = result.data?.influx?.isRunning && result.data.influx.operationType === 'Import';
                    setImportingDb(mysqlImporting || influxImporting);
                }
            } catch (e) {
                Sentry.captureException(e);
            }
        };

        pollStatus();
        const statusInterval = setInterval(pollStatus, 3000);
        return () => clearInterval(statusInterval);
    }, [apiFetch]);

    // Tick every second while any operation is running, purely to force a re-render
    // so the elapsed-time calculation below stays live.
    useEffect(() => {
        const anyRunning = dbStatus.mysql?.isRunning || dbStatus.influx?.isRunning;
        if (!anyRunning) return;
        const tickInterval = setInterval(() => setNowTick(Date.now()), 1000);
        return () => clearInterval(tickInterval);
    }, [dbStatus]);

    /**
     * Computes elapsed seconds for a given target ('mysql' | 'influx') based on the
     * server-reported startedAtUtc timestamp — NOT a local counter — so it survives
     * navigating away and back to this page.
     */
    const getDbElapsedSeconds = (target) => {
        const status = dbStatus[target];
        if (!status?.isRunning || !status.startedAtUtc) return 0;
        const startedMs = new Date(status.startedAtUtc).getTime();
        return Math.max(0, Math.floor((nowTick - startedMs) / 1000));
    };

    // Poll server-side import/export status so navigating away and back reflects reality,
    // and so we know a real import is running even if our local React state was reset.
    useEffect(() => {
        let intervalId;

        const pollStatus = async () => {
            try {
                const response = await apiFetch('/api/DatabaseBackup/status');
                const result = await response.json();
                if (result.success) {
                    const mysqlRunning = result.data?.mysql?.isRunning && result.data.mysql.operationType === 'Import';
                    const influxRunning = result.data?.influx?.isRunning && result.data.influx.operationType === 'Import';
                    setImportingDb(mysqlRunning || influxRunning);
                }
            } catch (e) {
                Sentry.captureException(e);
            }
        };

        pollStatus(); // check immediately on mount (handles navigate-away-and-back case)
        intervalId = setInterval(pollStatus, 3000); // keep polling every 3s while page is open

        return () => clearInterval(intervalId);
    }, [apiFetch]);

    /**
     * Toggle category expansion state
     */
    const toggleCategory = (category) => {
        setExpandedCategories(prev => ({
            ...prev,
            [category]: !prev[category]
        }));
    };

    /**
     * Handle clicking on a secret to edit it.
     * Secrets listed in MANAGED_SECRET_REDIRECTS are not editable from this page —
     * clicking their card navigates to the dedicated settings page instead.
     */
    const handleSecretClick = async (secretId) => {
        try {
            const response = await apiFetch(`/api/AppSecrets/${secretId}`);
            const data = await response.json();
            if (data.success) {
                // Block editing for managed secrets — redirect instead
                const managed = MANAGED_SECRET_REDIRECTS[data.data.secretKey];
                if (managed) {
                    onNavigateToTab?.(managed.tabId);
                    return;
                }
                setSelectedSecret(data.data);
                setEditSecret({ ...data.data });
                setIsCreateMode(false);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error(e);
            toast.error('Failed to load secret details');
        }
    };

    /**
     * Handle creating a new secret - show integration selector first
     */
    const handleCreateClick = () => {
        setShowIntegrationSelector(true);
    };

    /**
     * Handle integration selection from the selector
     */
    const handleIntegrationSelect = (integrationKey, defaults) => {
        const newSecret = {
            ...defaults,
            secretKey: defaults.secretKey || '',
            displayName: defaults.displayName || '',
            description: defaults.description || '',
            secretType: defaults.secretType || 'SimpleCredential',
            category: defaults.category || categories[0] || 'IT',
            isActive: true,
            username: '',
            password: '',
            apiKey: '',
            apiToken: '',
            // MySQL fields
            mySqlServer: '',
            mySqlPort: 3306,
            mySqlDatabase: '',
            mySqlOptions: '',
            // MS SQL fields
            msSqlServer: '',
            msSqlDatabase: '',
            msSqlUseWindowsAuth: false,
            msSqlEncrypt: false,
            msSqlTrustServerCertificate: false,
            msSqlConnectionTimeout: 30,
            msSqlCommandTimeout: 30,
            msSqlMaxPoolSize: 100,
            msSqlMinPoolSize: 5,
            msSqlOptions: '',
            // Azure AD fields
            azureTenantId: '',
            azureClientId: '',
            azureClientSecret: '',
            azureAuthority: '',
            azureScopes: '',
            // OAuth fields
            oAuthClientId: '',
            oAuthClientSecret: '',
            oAuthAuthUrl: '',
            oAuthTokenUrl: '',
            oAuthRefreshToken: '',
            oAuthScopes: '',
            // Email configuration fields
            emailProvider: 'SMTP',
            smtpServer: '',
            smtpPort: 587,
            smtpUseSsl: true,
            emailFromAddress: '',
            emailFromName: '',
            // Configuration fields
            baseUrl: '',
            secondaryUrl: '',
            timeoutSeconds: null,
            maxItems: null,
            maxParallelism: null,
            isEnabled: true,
            allowInsecure: false,
            domains: '',
            modelName: '',
            customerId: '',
            provider: '',
            pageSize: null,
            maxChars: null,
            maxTokens: null,
            // Expiry reminder fields
            createExpiryReminder: false,
            expiryReminderDays: 30,
            expiryReminderDepartmentId: null,
            // Other fields
            customData: '',
            notes: '',
            tags: '',
            expiryDate: null,
            // Credential reuse fields
            referencedSecretKey: '',
            inheritedFields: ''
        };
        setEditSecret(newSecret);
        setSelectedSecret(null);
        setIsCreateMode(true);
        setShowIntegrationSelector(false); // Close the selector
    };

    /**
     * Handle skipping integration selection (create generic secret)
     */
    const handleSkipIntegrationSelection = () => {
        const newSecret = {
            secretKey: '',
            displayName: '',
            description: '',
            secretType: 'SimpleCredential',
            category: categories[0] || 'IT',
            isActive: true,
            username: '',
            password: '',
            apiKey: '',
            apiToken: '',
            // MySQL fields
            mySqlServer: '',
            mySqlPort: 3306,
            mySqlDatabase: '',
            mySqlOptions: '',
            // MS SQL fields
            msSqlServer: '',
            msSqlDatabase: '',
            msSqlUseWindowsAuth: false,
            msSqlEncrypt: false,
            msSqlTrustServerCertificate: false,
            msSqlConnectionTimeout: 30,
            msSqlCommandTimeout: 30,
            msSqlMaxPoolSize: 100,
            msSqlMinPoolSize: 5,
            msSqlOptions: '',
            // Azure AD fields
            azureTenantId: '',
            azureClientId: '',
            azureClientSecret: '',
            azureAuthority: '',
            azureScopes: '',
            // OAuth fields
            oAuthClientId: '',
            oAuthClientSecret: '',
            oAuthAuthUrl: '',
            oAuthTokenUrl: '',
            oAuthRefreshToken: '',
            oAuthScopes: '',
            // Email configuration fields
            emailProvider: 'SMTP',
            smtpServer: '',
            smtpPort: 587,
            smtpUseSsl: true,
            emailFromAddress: '',
            emailFromName: '',
            // Configuration fields
            baseUrl: '',
            secondaryUrl: '',
            timeoutSeconds: null,
            maxItems: null,
            maxParallelism: null,
            isEnabled: true,
            allowInsecure: false,
            domains: '',
            modelName: '',
            customerId: '',
            provider: '',
            pageSize: null,
            maxChars: null,
            maxTokens: null,
            // Expiry reminder fields
            createExpiryReminder: false,
            expiryReminderDays: 30,
            expiryReminderDepartmentId: null,
            // Other fields
            customData: '',
            notes: '',
            tags: '',
            expiryDate: null,
            // Credential reuse fields
            referencedSecretKey: '',
            inheritedFields: ''
        };
        setEditSecret(newSecret);
        setSelectedSecret(null);
        setIsCreateMode(true);
        setShowIntegrationSelector(false); // Close the selector
    };

    /**
     * Close the modal and reset state
     */
    const closeModal = () => {
        setSelectedSecret(null);
        setEditSecret(null);
        setIsCreateMode(false);
        setSaving(false);
        setVisiblePasswords({});
        setTesting(false);
        setTestResult(null);
    };

    /**
     * Test credentials for the current secret
     */
    const handleTestCredentials = async () => {
        if (!editSecret) return;

        setTesting(true);
        setTestResult(null);

        try {
            // Determine the appropriate test endpoint based on secret key or type
            const secretKey = editSecret.secretKey?.toLowerCase() || '';
            const secretType = editSecret.secretType;
            
            let testEndpoint = null;
            let testBody = {};

            // Map secret keys to test endpoints
            if (secretKey === 'emailconfig' || secretType === 'EmailConfig') {
                testEndpoint = '/api/AppSecrets/test/email';
                testBody = {
                    provider: editSecret.emailProvider || 'SMTP',
                    smtpServer: editSecret.smtpServer,
                    smtpPort: editSecret.smtpPort,
                    smtpUseSsl: editSecret.smtpUseSsl,
                    username: editSecret.username,
                    password: editSecret.password,
                    fromEmail: editSecret.emailFromAddress,
                    fromName: editSecret.emailFromName,
                    tenantId: editSecret.azureTenantId,
                    clientId: editSecret.azureClientId,
                    clientSecret: editSecret.azureClientSecret
                };
            } else if (secretKey === 'invgate') {
                testEndpoint = '/api/AppSecrets/test/invgate';
                testBody = {
                    username: editSecret.username,
                    apiKey: editSecret.apiKey
                };
            } else if (secretKey === 'rubrik') {
                testEndpoint = '/api/AppSecrets/test/rubrik';
                testBody = {
                    clientId: editSecret.oAuthClientId,
                    clientSecret: editSecret.oAuthClientSecret
                };
            } else if (secretKey === 'proxmox') {
                testEndpoint = '/api/AppSecrets/test/proxmox';
                testBody = {
                    tokenId: editSecret.username,
                    tokenSecret: editSecret.password
                };
            } else if (secretKey === 'gemini' || secretKey.includes('gemini')) {
                testEndpoint = '/api/AppSecrets/test/gemini';
                testBody = {
                    apiKey: editSecret.apiKey
                };
            } else if (secretKey === 'github.token' || secretKey.includes('github')) {
                testEndpoint = '/api/AppSecrets/test/github';
                testBody = {
                    token: editSecret.password || editSecret.apiKey
                };
            } else if (secretKey === 'meteocontrol' || secretKey.includes('meteo')) {
                testEndpoint = '/api/AppSecrets/test/meteocontrol';
                testBody = {
                    apiKey: editSecret.apiKey,
                    username: editSecret.username,
                    password: editSecret.password
                };
            } else if (secretType === 'MySqlConnection' || secretType === 'MsSqlConnection') {
                testEndpoint = '/api/AppSecrets/test/database';
                testBody = {
                    secretKey: editSecret.secretKey,
                    databaseType: secretType === 'MySqlConnection' ? 'mysql' : 'mssql'
                };
            } else if (editSecret.id) {
                // For existing secrets, use the generic test-by-id endpoint
                testEndpoint = `/api/AppSecrets/${editSecret.id}/test`;
                testBody = {};
            } else {
                setTestResult({
                    success: false,
                    message: 'No test available for this credential type. Save the secret first to enable testing.'
                });
                setTesting(false);
                return;
            }

            const response = await apiFetch(testEndpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(testBody)
            });

            const data = await response.json();
            setTestResult({
                success: data.success,
                message: data.message || (data.success ? 'Test successful!' : 'Test failed')
            });

            if (data.success) {
                toast.success('Credential test passed!');
            } else {
                toast.error(data.message || 'Credential test failed');
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error('Test error:', e);
            setTestResult({
                success: false,
                message: `Test failed: ${e.message}`
            });
            toast.error('Failed to test credentials');
        } finally {
            setTesting(false);
        }
    };

    /**
     * Check if the current secret type supports testing
     */
    const canTestCredentials = () => {
        if (!editSecret) return false;
        
        const secretKey = editSecret.secretKey?.toLowerCase() || '';
        const secretType = editSecret.secretType;
        
        // List of testable secret keys
        const testableKeys = [
            'emailconfig', 'invgate', 'rubrik', 'proxmox',
            'gemini', 'github', 'meteocontrol'
        ];
        
        // Check if secret key matches any testable key
        if (testableKeys.some(key => secretKey.includes(key))) return true;
        
        // Check if it's an EmailConfig type
        if (secretType === 'EmailConfig') return true;
        
        // Check if it's a database connection type
        if (secretType === 'MySqlConnection' || secretType === 'MsSqlConnection') return true;
        
        // Check if it's an existing secret (can use generic test endpoint)
        if (editSecret.id) return true;
        
        return false;
    };

    /**
     * Auto-generate a PascalCase secret key from a display name.
     * Example: "My Great Secret" → "MyGreatSecret"
     */
    const generateSecretKey = (displayName) => {
        if (!displayName || !displayName.trim()) return '';
        return displayName
            .replace(/[^a-zA-Z0-9\s]/g, '')
            .split(/\s+/)
            .filter(Boolean)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join('');
    };

    /**
     * Handle field changes.
     * When creating a new secret, auto-generates the secretKey from the displayName.
     */
    const handleFieldChange = (field, value) => {
        setEditSecret(prev => {
            const updated = { ...prev, [field]: value };
            // Auto-generate secretKey from displayName when creating
            if (field === 'displayName' && isCreateMode) {
                updated.secretKey = generateSecretKey(value);
            }
            return updated;
        });
    };

    /**
     * Toggle password visibility for a specific field
     */
    const togglePasswordVisibility = (field) => {
        setVisiblePasswords(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    /**
     * Save the secret (create or update)
     */
    /**
     * Validates a URL to ensure it has proper format (http:// or https://)
     * @param {string} url - The URL to validate
     * @returns {boolean} - True if valid, false otherwise
     */
    const isValidUrl = (url) => {
        if (!url || !url.trim()) return true; // Empty is valid (optional field)
        try {
            const urlObj = new URL(url);
            return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
        } catch (catchError) {
            Sentry.captureException(catchError);
            return false;
        }
    };

    /**
     * Save or update a secret with validation
     */
    const handleSave = async () => {
        // Auto-generate secretKey from displayName if not already set. Build a fresh
        // object rather than mutating editSecret directly - it's a useState value, and
        // setEditSecret() below wouldn't be reflected in `editSecret` until the next
        // render anyway, so the rest of this function must work off the local copy.
        let secretToSave = editSecret;
        if (isCreateMode && (!editSecret.secretKey || !editSecret.secretKey.trim())) {
            if (editSecret.displayName && editSecret.displayName.trim()) {
                secretToSave = { ...editSecret, secretKey: generateSecretKey(editSecret.displayName) };
                setEditSecret(secretToSave);
            } else {
                toast.error("Display Name is required.");
                return;
            }
        }

        if (!secretToSave.secretKey || !secretToSave.secretKey.trim()) {
            toast.error("Display Name is required to generate an internal identifier.");
            return;
        }

        // Validate Base URL if provided
        if (secretToSave.baseUrl && secretToSave.baseUrl.trim()) {
            if (!isValidUrl(secretToSave.baseUrl)) {
                toast.error("Base URL must be a valid URL starting with http:// or https://");
                return;
            }
        }

        // Validate Secondary URL if provided
        if (secretToSave.secondaryUrl && secretToSave.secondaryUrl.trim()) {
            if (!isValidUrl(secretToSave.secondaryUrl)) {
                toast.error("Secondary URL must be a valid URL starting with http:// or https://");
                return;
            }
        }

        // Validate Azure AD fields - ensure Authority URL is not in Tenant ID field
        if (secretToSave.azureTenantId && secretToSave.azureTenantId.includes('://')) {
            toast.error("Azure Tenant ID should be a GUID only, not a full URL. Please move the Authority URL to the 'Authority URL' field.");
            return;
        }

        setSaving(true);
        try {
            const method = isCreateMode ? 'POST' : 'PUT';
            const url = isCreateMode ? '/api/AppSecrets' : `/api/AppSecrets/${secretToSave.id}`;

            const response = await apiFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(secretToSave)
            });

            const data = await response.json();
            if (response.ok) {
                toast.success(isCreateMode ? 'Secret created successfully' : 'Secret updated successfully');

                // Refresh AI configuration if Insights secret was modified
                if (secretToSave.secretKey === 'Insights') {
                    console.log('[SecretsPage] Refreshing AI configuration after Insights secret save');
                    await refreshConfig();
                }
                
                closeModal();
                fetchSecrets();
            } else {
                toast.error(data?.message || 'Failed to save secret');
            }
        } catch (e) {
            Sentry.captureException(e);
            toast.error('Error saving secret');
            console.error(e);
        } finally {
            setSaving(false);
        }
    };

    /**
     * Handle delete confirmation
     */
    const handleDeleteClick = (secret, event) => {
        event.stopPropagation();
        setDeleteTarget(secret);
    };

    /**
     * Confirm delete
     */
    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        try {
            const response = await apiFetch(`/api/AppSecrets/${deleteTarget.id}`, {
                method: 'DELETE'
            });
            if (response.ok) {
                toast.success('Secret deleted successfully');
                
                // Refresh AI configuration if Insights secret was deleted
                if (deleteTarget.secretKey === 'Insights') {
                    console.log('[SecretsPage] Refreshing AI configuration after Insights secret deletion');
                    await refreshConfig();
                }
                
                fetchSecrets();
            } else {
                toast.error('Failed to delete secret');
            }
        } catch (e) {
            Sentry.captureException(e);
            toast.error('Error deleting secret');
            console.error(e);
        } finally {
            setDeleteTarget(null);
        }
    };

    /**
     * Export secrets to JSON file
     */
    const handleExport = async () => {
        try {
            setExportingSecrets(true);
            const response = await apiFetch(`/api/AppSecrets/export?includePasswords=${exportIncludePasswords}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify([]) // Empty array = export all
            });
            
            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `AppSecrets_Export_${new Date().toISOString().split('T')[0]}.json`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                
                toast.success(exportIncludePasswords 
                    ? 'Secrets exported with passwords (keep secure!)' 
                    : 'Secrets exported (passwords redacted for security)');
                setShowExportModal(false);
            } else {
                toast.error('Failed to export secrets');
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error(e);
            toast.error('Error exporting secrets');
        } finally {
            setExportingSecrets(false);
        }
    };

    /**
     * Import secrets from JSON file
     */
    const handleImport = async () => {
        if (!importFile) {
            toast.error('Please select a file to import');
            return;
        }
        
        try {
            setImportingSecrets(true);
            const formData = new FormData();
            formData.append('file', importFile);
            
            const response = await apiFetch(`/api/AppSecrets/import?skipCicdManaged=true&overwriteExisting=${importOverwriteExisting}`, {
                method: 'POST',
                body: formData
            });
            
            const result = await response.json();
            
            if (result.success) {
                const summary = result.data;
                toast.success(
                    `Import complete! Created: ${summary.created.length}, Updated: ${summary.updated.length}, Skipped: ${summary.skipped.length}`
                );
                
                if (summary.warnings && summary.warnings.length > 0) {
                    summary.warnings.forEach(warning => toast.warn(warning));
                }
                
                setShowImportModal(false);
                setImportFile(null);
                fetchSecrets();
            } else {
                toast.error(result.message || 'Failed to import secrets');
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error(e);
            toast.error('Error importing secrets');
        } finally {
            setImportingSecrets(false);
        }
    };

    /**
     * Kick off a full MySQL or InfluxDB export as a background job on the server and return
     * immediately. The actual download happens later, automatically, once the poll-driven
     * dbStatus effect below sees isReadyForDownload flip to true.
     *
     * Why a job instead of a direct download: a full InfluxDB backup on a production-sized
     * bucket can run well over an hour. Holding a single HTTP request open (in a browser tab)
     * for that long doesn't survive any proxy in the path with a shorter timeout - which in
     * production reached a ~132GB bucket and consistently failed. Starting a background job
     * and polling status (the same status endpoint already used for import progress) means the
     * slow part never depends on one long-lived connection at all.
     */
    const handleDbExport = async (type) => {
        const setter = type === 'mysql' ? setExportingMySql : setExportingInflux;
        try {
            setter(true);
            exportDownloadClaimedRef.current[type] = false;

            const response = await apiFetch(`/api/DatabaseBackup/export/${type}/start`, { method: 'POST' });
            const result = await response.json();

            if (!response.ok || !result.success) {
                toast.error(result?.message || `Failed to start ${type === 'mysql' ? 'MySQL' : 'InfluxDB'} export.`);
                setter(false);
                return;
            }

            toast.info(
                `${type === 'mysql' ? 'MySQL' : 'InfluxDB'} export started — this can take a while for a large database. ` +
                `You can navigate away; the download will start automatically once it's ready.`
            );
            // Deliberately not resetting the spinner here - the dbStatus poll effect below
            // flips it off once the job actually finishes (ready or failed), same as import.
        } catch (e) {
            Sentry.captureException(e);
            console.error(e);
            toast.error('Error starting database export.');
            setter(false);
        }
    };

    /**
     * Fetches an already-completed export via a normal Blob download (Authorization header,
     * not a query-string token, and no same-tab navigation) once the background job reports
     * isReadyForDownload. The server only offers each completed file once (it clears the ready
     * flag as soon as this request lands), so exportDownloadClaimedRef just protects against
     * firing this more than once client-side across overlapping 3s poll ticks.
     */
    useEffect(() => {
        const claimCompletedExport = async (type, setter) => {
            const status = dbStatus[type];
            if (!status) return;

            if (status.errorMessage && !status.isRunning) {
                if (exportDownloadClaimedRef.current[type]) return; // already surfaced
                exportDownloadClaimedRef.current[type] = true;
                toast.error(`${type === 'mysql' ? 'MySQL' : 'InfluxDB'} export failed: ${status.errorMessage}`);
                setter(false);
                return;
            }

            if (!status.isReadyForDownload || !status.resultFileName) return;
            if (exportDownloadClaimedRef.current[type]) return;
            exportDownloadClaimedRef.current[type] = true;

            try {
                const response = await apiFetch(`/api/DatabaseBackup/export/${type}/download`, {}, 3600000);
                if (!response.ok) {
                    toast.error(`Failed to download the completed ${type === 'mysql' ? 'MySQL' : 'InfluxDB'} export.`);
                    return;
                }

                const blob = await response.blob();
                const objectUrl = URL.createObjectURL(blob);

                const disposition = response.headers.get('Content-Disposition') || '';
                const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
                const fileName = filenameMatch?.[1] || status.resultFileName;

                const a = document.createElement('a');
                a.href = objectUrl;
                a.download = fileName;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);

                toast.success(`${type === 'mysql' ? 'MySQL' : 'InfluxDB'} export downloaded.`);
            } catch (e) {
                Sentry.captureException(e);
                console.error(e);
                toast.error(`Error downloading the completed ${type === 'mysql' ? 'MySQL' : 'InfluxDB'} export.`);
            } finally {
                setter(false);
            }
        };

        claimCompletedExport('mysql', setExportingMySql);
        claimCompletedExport('influx', setExportingInflux);
    }, [dbStatus, apiFetch]);

    /**
     * Completely overwrite the current environment's MySQL or InfluxDB database.
     * Requires the user to type the environment name to confirm (destructive operation).
     */
    const handleDbImport = async () => {
        const type = showDbImportModal;
        if (!dbImportFile) {
            toast.error('Please select a file to import');
            return;
        }
        if (dbImportConfirmText.trim().toUpperCase() !== dbEnvironment.trim().toUpperCase()) {
            toast.error(`Type "${dbEnvironment}" to confirm this destructive overwrite.`);
            return;
        }

        // Close the modal immediately — progress now shows on the main panel instead
        const fileToImport = dbImportFile;
        setShowDbImportModal(null);
        setDbImportFile(null);
        setDbImportConfirmText('');

        try {
            setImportingDb(true);
            const formData = new FormData();
            formData.append('file', fileToImport);

            const response = await apiFetch(`/api/DatabaseBackup/import/${type}`, {
                method: 'POST',
                body: formData
            });

            const result = await response.json();
            if (result.success) {
                toast.success(result.message);
            } else {
                toast.error(result.message || 'Import failed.');
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error(e);
            toast.error('Error importing database.');
        } finally {
            setImportingDb(false);
        }
    };

    /**
     * Check if a secret has missing credentials (validation)
     */
    const hasIncompleteCreds = (secret) => {
        // Check for database connections with no password
        if (secret.secretType === 'MsSqlConnection' || secret.secretType === 'MySqlConnection') {
            if (!secret.hasPassword) return true;
        }

        // Check for Azure AD with missing credentials
        // API uses camelCase JSON serialization (JsonNamingPolicy.CamelCase)
        const secretKeyValue = secret.secretKey || secret.SecretKey;
        if (secretKeyValue === 'AzureAd') {
            if (!secret.azureTenantId || !secret.azureClientId || !secret.hasAzureClientSecret) {
                return true;
            }
        }

        // Check for EmailConfig with missing credentials
        if (secret.secretType === 'EmailConfig') {
            const emailProvider = secret.emailProvider;
            if (emailProvider === 'Microsoft365') {
                // If EmailConfig references AzureAd, it inherits credentials - not incomplete
                const referencedKey = secret.referencedSecretKey;
                if (referencedKey === 'AzureAd') {
                    return false; // Credentials are inherited, not incomplete
                }
                // Only check direct credentials if not using reference
                if (!secret.azureTenantId || !secret.azureClientId || !secret.hasAzureClientSecret) {
                    return true;
                }
            } else if (!secret.hasUsername || !secret.hasPassword) {
                return true;
            }
        }

        // Check for SimpleCredential with no username or password
        if (secret.secretType === 'SimpleCredential') {
            if (!secret.hasUsername || !secret.hasPassword) return true;
        }

        // Check for ApiKey with no API key
        if (secret.secretType === 'ApiKey') {
            if (!secret.hasApiKey) return true;
        }

        return false;
    };

    /**
     * Get a list of human-readable missing field labels for an incomplete secret.
     * Uses integration config required fields when available, otherwise falls back
     * to the same checks as hasIncompleteCreds.
     */
    const getMissingFields = (secret) => {
        if (!secret) return [];
        const missing = [];
        const secretKey = (secret.secretKey || secret.SecretKey || '').toLowerCase();
        const integrationConfig = getIntegrationConfig(secretKey);

        if (integrationConfig) {
            // Use integration config required fields for precise reporting.
            // When the modal is open, editSecret has actual decrypted values from the
            // detail endpoint (e.g. secret.azureClientSecret = "abc..."), not just the
            // boolean flags from the summary endpoint (secret.hasAzureClientSecret).
            // Check the actual value first, then fall back to the boolean flag.
            for (const field of integrationConfig.fields) {
                if (!field.required) continue;
                if (!shouldShowField(field, secret)) continue;
                const value = getFieldValue(secret, field.key);
                const isPasswordField = field.type === 'password';
                if (isPasswordField) {
                    // Detail data has actual values; summary data has has* flags
                    const hasActualValue = !!value;
                    const hasFlag = field.key === 'password' ? secret.hasPassword
                        : field.key === 'apiKey' ? secret.hasApiKey
                        : field.key === 'azureClientSecret' ? secret.hasAzureClientSecret
                        : field.key === 'oAuthClientSecret' ? secret.hasPassword
                        : false;
                    if (!hasActualValue && !hasFlag) missing.push(field.label);
                } else {
                    if (!value && value !== 0 && value !== false) missing.push(field.label);
                }
            }
        } else {
            // Fallback: mirror hasIncompleteCreds logic with readable labels.
            // API uses camelCase JSON serialization.
            const secretType = secret.secretType;
            if (secretType === 'MsSqlConnection' || secretType === 'MySqlConnection') {
                if (!secret.hasPassword && !secret.password) missing.push('Password');
            }
            if (secretKey === 'azuread') {
                if (!secret.azureTenantId) missing.push('Azure Tenant ID');
                if (!secret.azureClientId) missing.push('Azure Client ID');
                if (!secret.hasAzureClientSecret && !secret.azureClientSecret) missing.push('Azure Client Secret');
            }
            if (secretType === 'EmailConfig') {
                const emailProvider = secret.emailProvider;
                if (emailProvider === 'Microsoft365') {
                    const referencedKey = secret.referencedSecretKey;
                    if (referencedKey !== 'AzureAd') {
                        if (!secret.azureTenantId) missing.push('Azure Tenant ID');
                        if (!secret.azureClientId) missing.push('Azure Client ID');
                        if (!secret.hasAzureClientSecret && !secret.azureClientSecret) missing.push('Azure Client Secret');
                    }
                } else {
                    if (!secret.hasUsername && !secret.username) missing.push('Username');
                    if (!secret.hasPassword && !secret.password) missing.push('Password');
                }
            }
            if (secretType === 'SimpleCredential') {
                if (!secret.hasUsername && !secret.username) missing.push('Username');
                if (!secret.hasPassword && !secret.password) missing.push('Password');
            }
            if (secretType === 'ApiKey') {
                if (!secret.hasApiKey && !secret.apiKey) missing.push('API Key');
            }
        }
        return missing;
    };

    /**
     * Render form fields based on secret type or integration configuration
     */
    const renderFormFields = () => {
        if (!editSecret) return null;

        const secretType = editSecret.secretType;
        const secretKey = editSecret.secretKey?.toLowerCase() || '';

        // Check if we have a specific integration configuration for this secret
        const integrationConfig = getIntegrationConfig(secretKey);

        // If we have an integration config, use the simplified form system
        const useSimplifiedForm = integrationConfig !== null;

        return (
            <div className="secret-form">
                {/* Integration-Specific Simplified Form */}
                {useSimplifiedForm && integrationConfig && (
                    <>
                        {/* Integration Header */}
                        <div className="form-section" style={{backgroundColor: '#f0f8ff', borderLeft: '4px solid #0066cc'}}>
                            <h3 style={{color: '#0066cc', marginBottom: '12px'}}>
                                📋 {integrationConfig.name}
                            </h3>
                            <div style={{marginBottom: '12px'}}>
                                <strong>Category:</strong> <span style={{color: '#0066cc'}}>{integrationConfig.category}</span>
                            </div>
                            <div style={{marginBottom: '8px'}}>
                                {integrationConfig.description}
                            </div>
                            <div style={{marginBottom: '12px'}}>
                                <strong>Secret Type:</strong> <span style={{color: '#0066cc'}}>{integrationConfig.secretType}</span>
                                {secretType && secretType !== integrationConfig.secretType && (
                                    <span style={{color: '#e67700', marginLeft: '8px'}}>
                                        ⚠️ Current type ({secretType}) differs from recommended. Consider updating to {integrationConfig.secretType}.
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Basic Information for Integration */}
                        <div className="form-section">
                            <h3>Basic Information</h3>
                            <div className="form-row">
                                <div className="form-field">
                                    <label>Display Name *</label>
                                    <input
                                        type="text"
                                        value={editSecret.displayName || ''}
                                        onChange={e => handleFieldChange('displayName', e.target.value)}
                                        placeholder={integrationConfig.name}
                                        required
                                    />
                                    <small className="field-help">Human-friendly name for this integration. A unique internal identifier is generated automatically.</small>
                                    {editSecret.secretKey && (
                                        <small className="field-help" style={{ color: '#999', fontStyle: 'italic' }}>
                                            Internal ID: {editSecret.secretKey}
                                        </small>
                                    )}
                                </div>
                                <div className="form-field" />
                            </div>
                            <div className="form-field full-width">
                                <label>Description</label>
                                <textarea
                                    value={editSecret.description || ''}
                                    onChange={e => handleFieldChange('description', e.target.value)}
                                    placeholder={`Integration purpose: ${integrationConfig.description}`}
                                    rows={2}
                                />
                            </div>
                        </div>

                        {/* Integration-Specific Fields using new renderer */}
                        <div className="form-section">
                            <h3>Integration Configuration</h3>
                            <div className="form-row">
                                <IntegrationFieldRenderer
                                    fields={integrationConfig.fields}
                                    secret={editSecret}
                                    onFieldChange={handleFieldChange}
                                    visiblePasswords={visiblePasswords}
                                    onTogglePasswordVisibility={togglePasswordVisibility}
                                    availableSecrets={secretsByCategory}
                                    isValidUrl={isValidUrl}
                                />
                            </div>
                        </div>
                    </>
                )}

                {/* Fall back to traditional form for non-integration secrets or when no config exists */}
                {!useSimplifiedForm && (
                    <>
                        {/* Common fields for all types */}
                        <div className="form-section">
                    <h3>Basic Information</h3>
                    <div className="form-row">
                        <div className="form-field">
                            <label>Display Name *</label>
                            <input
                                type="text"
                                value={editSecret.displayName || ''}
                                onChange={e => handleFieldChange('displayName', e.target.value)}
                                placeholder="e.g., MeteoControl Weather API, Azure AD Application"
                                required
                            />
                            <small className="field-help">Human-friendly name for this secret. A unique internal identifier is generated automatically.</small>
                            {editSecret.secretKey && (
                                <small className="field-help" style={{ color: '#999', fontStyle: 'italic' }}>
                                    Internal ID: {editSecret.secretKey}
                                </small>
                            )}
                        </div>
                        <div className="form-field">
                            <label>Secret Type *</label>
                            <select
                                value={editSecret.secretType || 'SimpleCredential'}
                                onChange={e => handleFieldChange('secretType', e.target.value)}
                                disabled={!isCreateMode}
                            >
                                {secretTypes.map(type => (
                                    <option key={type.value} value={type.value}>{type.label}</option>
                                ))}
                            </select>
                            {!isCreateMode && (
                                <small className="field-help" style={{ color: '#e67700' }}>Type cannot be changed after creation</small>
                            )}
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="form-field">
                            <label>Category *</label>
                            <select
                                value={editSecret.category || ''}
                                onChange={e => handleFieldChange('category', e.target.value)}
                                disabled={!isCreateMode}
                            >
                                <option value="">Select category...</option>
                                {categories.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                            {isCreateMode ? (
                                <small className="field-help">Choose IT, External API, Database, Azure, etc. based on purpose</small>
                            ) : (
                                <small className="field-help" style={{ color: '#e67700' }}>Category cannot be changed after creation to prevent unintended side effects</small>
                            )}
                        </div>
                        <div className="form-field" />
                    </div>
                    <div className="form-field full-width">
                        <label>Description</label>
                        <textarea
                            value={editSecret.description || ''}
                            onChange={e => handleFieldChange('description', e.target.value)}
                            placeholder="What is this secret used for?"
                            rows={2}
                        />
                    </div>
                </div>

                {/* Credential Reuse Section */}
                <div className="form-section" style={{backgroundColor: '#f9f9f9', border: '1px solid #e0e0e0'}}>
                    <h3>Credential Reuse (Optional)</h3>
                    <div className="form-field full-width">
                        <label>Reference Another Secret</label>
                        <select
                            value={editSecret.referencedSecretKey || ''}
                            onChange={e => handleFieldChange('referencedSecretKey', e.target.value)}
                        >
                            <option value="">-- No Reference (Use Own Credentials) --</option>
                            {Object.entries(secretsByCategory).flatMap(([, secrets]) =>
                                secrets
                                    .filter(s => s.id !== editSecret.id) // Don't allow self-reference
                                    .filter(s => ['AzureAd', 'OAuth', 'SimpleCredential', 'ApiKey'].includes(s.secretType)) // Only credential types
                                    .map(s => (
                                        <option key={s.id} value={s.secretKey}>
                                            {s.displayName || s.secretKey} ({s.secretType})
                                        </option>
                                    ))
                            )}
                        </select>
                        <small className="field-help">
                            Reuse credentials from another secret instead of duplicating them. 
                            Example: Multiple EmailConfig secrets can reference the same AzureAd credentials.
                        </small>
                    </div>
                    {editSecret.referencedSecretKey && (
                        <div className="form-field full-width" style={{marginTop: '12px'}}>
                            <label>Inherited Fields (Optional)</label>
                            <input
                                type="text"
                                value={editSecret.inheritedFields || ''}
                                onChange={e => handleFieldChange('inheritedFields', e.target.value)}
                                placeholder="Leave empty to inherit all credentials, or specify: AzureTenantId,AzureClientId,AzureClientSecret"
                            />
                            <small className="field-help">
                                Comma-separated list of specific fields to inherit. Leave empty to inherit all applicable credentials.
                            </small>
                        </div>
                    )}
                    {editSecret.referencedSecretKey && (
                        <div style={{marginTop: '12px', padding: '12px', backgroundColor: '#e7f3ff', borderRadius: '4px', border: '1px solid #b3d7ff'}}>
                            <strong>ℹ️ Credential Reference Active</strong>
                            <p style={{marginTop: '8px', marginBottom: 0, fontSize: '0.9em'}}>
                                This secret will inherit credentials from <strong>{editSecret.referencedSecretKey}</strong>.
                                You only need to fill in fields specific to this integration (like EmailFromAddress for email configs).
                                Referenced credentials are automatically merged at runtime.
                            </p>
                        </div>
                    )}
                </div>

                {/* Type-specific fields */}
                {(secretType === 'SimpleCredential' || secretType === 'MySqlConnection' || secretType === 'MsSqlConnection') && (
                    <div className="form-section">
                        <h3>Credentials</h3>
                        <div className="form-row">
                            <div className="form-field">
                                <label>Username</label>
                                <input
                                    type="text"
                                    value={editSecret.username || ''}
                                    onChange={e => handleFieldChange('username', e.target.value)}
                                />
                                {editSecret.secretKey?.toLowerCase() === 'proxmox' && (
                                    <small className="field-help">Token ID (e.g., user@realm!tokenid)</small>
                                )}
                            </div>
                            <div className="form-field password-field">
                                <label>Password</label>
                                <div className="password-input-group">
                                    <input
                                        type={visiblePasswords['password'] ? 'text' : 'password'}
                                        value={editSecret.password || ''}
                                        onChange={e => handleFieldChange('password', e.target.value)}
                                    />
                                    <button
                                        type="button"
                                        className="toggle-password"
                                        onClick={() => togglePasswordVisibility('password')}
                                    >
                                        {visiblePasswords['password'] ? <IoEyeOff /> : <IoEye />}
                                    </button>
                                </div>
                                {editSecret.secretKey?.toLowerCase() === 'proxmox' && (
                                    <small className="field-help">Token Secret</small>
                                )}
                            </div>
                        </div>
                        {/* Add API Key field for Porta integration */}
                        {secretType === 'SimpleCredential' && (editSecret.secretKey?.toLowerCase() === 'porta' || editSecret.secretKey?.toLowerCase().startsWith('porta.')) && (
                            <div className="form-field password-field full-width" style={{marginTop: '16px'}}>
                                <label>API Token</label>
                                <div className="password-input-group">
                                    <input
                                        type={visiblePasswords['apiKey'] ? 'text' : 'password'}
                                        value={editSecret.apiKey || ''}
                                        onChange={e => handleFieldChange('apiKey', e.target.value)}
                                    />
                                    <button
                                        type="button"
                                        className="toggle-password"
                                        onClick={() => togglePasswordVisibility('apiKey')}
                                    >
                                        {visiblePasswords['apiKey'] ? <IoEyeOff /> : <IoEye />}
                                    </button>
                                </div>
                                <small className="field-help">PortaOne API Token (if available)</small>
                            </div>
                        )}
                        {/* Add BaseUrl for integrations that need it */}
                        {secretType === 'SimpleCredential' && ['proxmox', 'invgate', 'rms', 'porta'].some(key => editSecret.secretKey?.toLowerCase().includes(key)) && (
                            <>
                                <div className="form-field full-width" style={{marginTop: '16px'}}>
                                    <label>Base URL</label>
                                    <input
                                        type="text"
                                        value={editSecret.baseUrl || ''}
                                        onChange={e => handleFieldChange('baseUrl', e.target.value)}
                                        placeholder={
                                            editSecret.secretKey?.toLowerCase() === 'proxmox' ? 'https://proxmox.example.com:8006/api2/json' :
                                            editSecret.secretKey?.toLowerCase() === 'invgate' ? 'https://invgate.example.com/api' :
                                            editSecret.secretKey?.toLowerCase() === 'rms' ? 'https://meters.rmsconnect.net/api' :
                                            (editSecret.secretKey?.toLowerCase() === 'porta' || editSecret.secretKey?.toLowerCase().startsWith('porta.')) ? 'https://porta.example.com:8444/soap/' :
                                            'https://api.example.com'
                                        }
                                        style={{
                                            borderColor: editSecret.baseUrl && !isValidUrl(editSecret.baseUrl) ? 'red' : undefined
                                        }}
                                    />
                                    <small className="field-help">
                                        API endpoint base URL (must start with http:// or https://)
                                    </small>
                                    {editSecret.baseUrl && !isValidUrl(editSecret.baseUrl) && (
                                        <small style={{ color: 'red', display: 'block', marginTop: '4px' }}>
                                            Invalid URL format. Must start with http:// or https://
                                        </small>
                                    )}
                                </div>
                                {editSecret.secretKey?.toLowerCase() === 'proxmox' && (
                                    <div className="form-row" style={{marginTop: '16px'}}>
                                        <label className="checkbox-label">
                                            <input
                                                type="checkbox"
                                                checked={editSecret.allowInsecure === true}
                                                onChange={e => handleFieldChange('allowInsecure', e.target.checked)}
                                            />
                                            Allow Insecure Connections (self-signed certificates)
                                        </label>
                                    </div>
                                )}
                                {/* Integration-specific hints */}
                                {editSecret.secretKey?.toLowerCase() === 'proxmox' && (
                                    <div className="integration-hint" style={{marginTop: '16px', padding: '12px', backgroundColor: '#e7f3ff', borderRadius: '4px', border: '1px solid #b3d7ff'}}>
                                        <strong>Proxmox Integration:</strong>
                                        <ul style={{marginTop: '8px', marginBottom: 0, paddingLeft: '20px'}}>
                                            <li>Token ID (Username): API token ID in format user@realm!tokenid</li>
                                            <li>Token Secret (Password): The secret for the API token</li>
                                            <li>Base URL: Proxmox API endpoint (e.g., https://host:8006/api2/json)</li>
                                            <li>Allow Insecure: Enable if using self-signed certificates</li>
                                        </ul>
                                    </div>
                                )}
                                {editSecret.secretKey?.toLowerCase() === 'invgate' && (
                                    <div className="integration-hint" style={{marginTop: '16px', padding: '12px', backgroundColor: '#e7f3ff', borderRadius: '4px', border: '1px solid #b3d7ff'}}>
                                        <strong>InvGate Integration:</strong>
                                        <ul style={{marginTop: '8px', marginBottom: 0, paddingLeft: '20px'}}>
                                            <li>Username: InvGate account username</li>
                                            <li>API Key: InvGate API authentication key</li>
                                            <li>Base URL: InvGate API endpoint</li>
                                        </ul>
                                    </div>
                                )}
                                {(editSecret.secretKey?.toLowerCase() === 'porta' || editSecret.secretKey?.toLowerCase().startsWith('porta.')) && (
                                    <>
                                        {/* PortaOne-specific configuration fields */}
                                        <div className="form-row" style={{marginTop: '16px'}}>
                                            <div className="form-field">
                                                <label>Customer ID</label>
                                                <input
                                                    type="text"
                                                    value={editSecret.customerId || ''}
                                                    onChange={e => handleFieldChange('customerId', e.target.value)}
                                                    placeholder="1878"
                                                />
                                                <small className="field-help">PortaOne customer identifier</small>
                                            </div>
                                            <div className="form-field">
                                                <label>Max Items (CDR Rows)</label>
                                                <input
                                                    type="number"
                                                    value={editSecret.maxItems || ''}
                                                    onChange={e => handleFieldChange('maxItems', e.target.value ? parseInt(e.target.value, 10) : null)}
                                                    placeholder="100000"
                                                />
                                                <small className="field-help">Maximum CDR records per request</small>
                                            </div>
                                        </div>
                                        <div className="form-field" style={{marginTop: '16px'}}>
                                            <label>Timeout (seconds)</label>
                                            <input
                                                type="number"
                                                value={editSecret.timeoutSeconds || ''}
                                                onChange={e => handleFieldChange('timeoutSeconds', e.target.value ? parseInt(e.target.value, 10) : null)}
                                                placeholder="300"
                                            />
                                            <small className="field-help">API request timeout in seconds</small>
                                        </div>
                                        <div className="integration-hint" style={{marginTop: '16px', padding: '12px', backgroundColor: '#e7f3ff', borderRadius: '4px', border: '1px solid #b3d7ff'}}>
                                            <strong>PortaOne Integration:</strong>
                                            <ul style={{marginTop: '8px', marginBottom: 0, paddingLeft: '20px'}}>
                                                <li>Username: PortaOne account username</li>
                                                <li>Password: PortaOne account password</li>
                                                <li>API Token: Optional API token for enhanced security</li>
                                                <li>Base URL: SOAP API endpoint (e.g., https://porta.example.com:8444/soap/)</li>
                                                <li>Customer ID: Your PortaOne customer identifier</li>
                                                <li>Max Items: Maximum CDR rows to fetch per request (default: 100000)</li>
                                                <li>Timeout: API timeout in seconds (default: 300)</li>
                                            </ul>
                                        </div>
                                    </>
                                )}
                                {(editSecret.secretKey?.toLowerCase() === 'rms' || editSecret.secretKey?.toLowerCase().startsWith('rms.')) && (
                                    <div className="integration-hint" style={{marginTop: '16px', padding: '12px', backgroundColor: '#e7f3ff', borderRadius: '4px', border: '1px solid #b3d7ff'}}>
                                        <strong>RMS Meters Integration:</strong>
                                        <ul style={{marginTop: '8px', marginBottom: 0, paddingLeft: '20px'}}>
                                            <li>Username: RMS account username/email</li>
                                            <li>Password: RMS account password</li>
                                            <li>Base URL: RMS API endpoint (e.g., https://meters.rmsconnect.net/api)</li>
                                        </ul>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {secretType === 'ApiKey' && (
                    <div className="form-section">
                        {/* Hide generic API Key field for GitHub since it uses structured fields */}
                        {!(editSecret.secretKey?.toLowerCase() === 'github' || editSecret.secretKey?.toLowerCase().startsWith('github.')) && (
                            <>
                                <h3>API Key</h3>
                                <div className="form-field password-field full-width">
                                    <label>API Key</label>
                                    <div className="password-input-group">
                                        <textarea
                                            value={editSecret.apiKey || ''}
                                            onChange={e => handleFieldChange('apiKey', e.target.value)}
                                            rows={3}
                                            style={{ fontFamily: visiblePasswords['apiKey'] ? 'inherit' : 'monospace' }}
                                        />
                                        <button
                                            type="button"
                                            className="toggle-password"
                                            onClick={() => togglePasswordVisibility('apiKey')}
                                        >
                                            {visiblePasswords['apiKey'] ? <IoEyeOff /> : <IoEye />}
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                        
                        {/* GitHub Personal Access Token Configuration */}
                        {(editSecret.secretKey?.toLowerCase() === 'github' || editSecret.secretKey?.toLowerCase().startsWith('github.')) && (
                            <>
                                <div className="integration-hint" style={{marginTop: '16px', marginBottom: '16px', padding: '12px', backgroundColor: '#e7f3ff', borderRadius: '4px', border: '1px solid #b3d7ff'}}>
                                    <strong>🔒 Secure GitHub Configuration</strong>
                                    <p style={{marginTop: '8px', marginBottom: 0}}>
                                        Your GitHub Personal Access Token is stored securely in the Password field (encrypted in database).
                                        The API Key field stores your default repository name, and Username stores the default owner.
                                    </p>
                                </div>
                                
                                <h4 style={{marginTop: '20px', marginBottom: '12px', color: '#555'}}>
                                    GitHub Credentials (Secure)
                                    <small style={{fontWeight: 'normal', display: 'block', color: '#666', fontSize: '0.9em', marginTop: '4px'}}>
                                        Personal Access Token with repo and workflow scopes
                                    </small>
                                </h4>
                                
                                <div className="form-field password-field full-width">
                                    <label>Personal Access Token</label>
                                    <div className="password-input-group">
                                        <input
                                            type={visiblePasswords['password'] ? 'text' : 'password'}
                                            value={editSecret.password || ''}
                                            onChange={e => handleFieldChange('password', e.target.value)}
                                            placeholder="github_pat_..."
                                        />
                                        <button
                                            type="button"
                                            className="toggle-password"
                                            onClick={() => togglePasswordVisibility('password')}
                                        >
                                            {visiblePasswords['password'] ? <IoEyeOff /> : <IoEye />}
                                        </button>
                                    </div>
                                    <small className="field-help">
                                        Generate from GitHub Settings → Developer settings → Personal access tokens
                                    </small>
                                </div>
                                
                                <h4 style={{marginTop: '20px', marginBottom: '12px', color: '#555'}}>
                                    Default Repository Settings
                                </h4>
                                
                                <div className="form-row">
                                    <div className="form-field">
                                        <label>Default Owner</label>
                                        <input
                                            type="text"
                                            value={editSecret.username || ''}
                                            onChange={e => handleFieldChange('username', e.target.value)}
                                            placeholder="Capital-Land-Management-Services"
                                        />
                                        <small className="field-help">Organization or username</small>
                                    </div>
                                    <div className="form-field">
                                        <label>Default Repository</label>
                                        <input
                                            type="text"
                                            value={editSecret.apiKey || ''}
                                            onChange={e => handleFieldChange('apiKey', e.target.value)}
                                            placeholder="CapTrac-ReactCore"
                                        />
                                        <small className="field-help">Repository name</small>
                                    </div>
                                </div>
                            </>
                        )}
                        
                        {/* Additional credentials for integrations that need username/password along with API key */}
                        {['meteocontrol', 'invgate'].some(key => editSecret.secretKey?.toLowerCase() === key || editSecret.secretKey?.toLowerCase().startsWith(key + '.')) && (
                            <>
                                <h4 style={{marginTop: '20px', marginBottom: '12px', color: '#555'}}>
                                    Additional Credentials
                                    <small style={{fontWeight: 'normal', display: 'block', color: '#666', fontSize: '0.9em', marginTop: '4px'}}>
                                        {(editSecret.secretKey?.toLowerCase() === 'meteocontrol' || editSecret.secretKey?.toLowerCase().startsWith('meteocontrol.')) && 'MeteoControl requires API Key AND basic authentication'}
                                        {(editSecret.secretKey?.toLowerCase() === 'invgate' || editSecret.secretKey?.toLowerCase().startsWith('invgate.')) && 'InvGate requires API Key AND username for authentication'}
                                    </small>
                                </h4>
                                <div className="form-row">
                                    <div className="form-field">
                                        <label>Username</label>
                                        <input
                                            type="text"
                                            value={editSecret.username || ''}
                                            onChange={e => handleFieldChange('username', e.target.value)}
                                            placeholder={(editSecret.secretKey?.toLowerCase() === 'meteocontrol' || editSecret.secretKey?.toLowerCase().startsWith('meteocontrol.')) ? 'MeteoControl username' : 'InvGate username'}
                                        />
                                    </div>
                                    {(editSecret.secretKey?.toLowerCase() === 'meteocontrol' || editSecret.secretKey?.toLowerCase().startsWith('meteocontrol.')) && (
                                        <div className="form-field password-field">
                                            <label>Password</label>
                                            <div className="password-input-group">
                                                <input
                                                    type={visiblePasswords['password'] ? 'text' : 'password'}
                                                    value={editSecret.password || ''}
                                                    onChange={e => handleFieldChange('password', e.target.value)}
                                                />
                                                <button
                                                    type="button"
                                                    className="toggle-password"
                                                    onClick={() => togglePasswordVisibility('password')}
                                                >
                                                    {visiblePasswords['password'] ? <IoEyeOff /> : <IoEye />}
                                                </button>
                                            </div>
                                            <small className="field-help">MeteoControl password for basic auth</small>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                        
                        {/* Hide Base URL for GitHub since it's not needed */}
                        {!(editSecret.secretKey?.toLowerCase() === 'github' || editSecret.secretKey?.toLowerCase().startsWith('github.')) && (
                            <div className="form-field full-width" style={{marginTop: '16px'}}>
                                <label>Base URL</label>
                                <input
                                    type="text"
                                    value={editSecret.baseUrl || ''}
                                    onChange={e => handleFieldChange('baseUrl', e.target.value)}
                                    placeholder="https://api.example.com"
                                    style={{
                                        borderColor: editSecret.baseUrl && !isValidUrl(editSecret.baseUrl) ? 'red' : undefined
                                    }}
                                />
                                <small className="field-help">
                                    API endpoint base URL (must start with http:// or https://)
                                </small>
                                {editSecret.baseUrl && !isValidUrl(editSecret.baseUrl) && (
                                    <small style={{ color: 'red', display: 'block', marginTop: '4px' }}>
                                        Invalid URL format. Must start with http:// or https://
                                    </small>
                                )}
                            </div>
                        )}
                        
                        {/* Additional fields for specific integrations */}
                        {editSecret.secretKey?.toLowerCase() === 'retaildynamics' && (
                            <div className="integration-hint" style={{marginTop: '16px', padding: '12px', backgroundColor: '#e7f3ff', borderRadius: '4px', border: '1px solid #b3d7ff'}}>
                                <strong>RetailDynamics Integration:</strong>
                                <ul style={{marginTop: '8px', marginBottom: 0, paddingLeft: '20px'}}>
                                    <li>API Key: Your RetailDynamics API authentication key</li>
                                    <li>Base URL: RetailDynamics API endpoint (e.g., https://api.retaildynamics.com)</li>
                                </ul>
                            </div>
                        )}
                        {editSecret.secretKey?.toLowerCase() === 'gemini' && (
                            <>
                                <div className="form-field full-width" style={{marginTop: '16px'}}>
                                    <label>Model Name</label>
                                    <input
                                        type="text"
                                        value={editSecret.modelName || ''}
                                        onChange={e => handleFieldChange('modelName', e.target.value)}
                                        placeholder="gemini-2.5-flash"
                                    />
                                    <small className="field-help">Gemini model to use (e.g., gemini-2.5-flash, gemini-pro)</small>
                                </div>
                                <div className="integration-hint" style={{marginTop: '16px', padding: '12px', backgroundColor: '#e7f3ff', borderRadius: '4px', border: '1px solid #b3d7ff'}}>
                                    <strong>Google Gemini Integration:</strong>
                                    <ul style={{marginTop: '8px', marginBottom: 0, paddingLeft: '20px'}}>
                                        <li>API Key: Your Google AI Studio API key</li>
                                        <li>Model Name: The Gemini model identifier to use</li>
                                    </ul>
                                </div>
                            </>
                        )}
                        {(editSecret.secretKey?.toLowerCase() === 'meteocontrol' || editSecret.secretKey?.toLowerCase().startsWith('meteocontrol.')) && (
                            <div className="integration-hint" style={{marginTop: '16px', padding: '12px', backgroundColor: '#e7f3ff', borderRadius: '4px', border: '1px solid #b3d7ff'}}>
                                <strong>MeteoControl Integration:</strong>
                                <ul style={{marginTop: '8px', marginBottom: 0, paddingLeft: '20px'}}>
                                    <li>API Key: X-API-KEY header value</li>
                                    <li>Username: Basic auth username</li>
                                    <li>Password: Basic auth password</li>
                                    <li>Base URL: MeteoControl API endpoint (e.g., https://api.meteocontrol.de/v2)</li>
                                </ul>
                            </div>
                        )}
                        {(editSecret.secretKey?.toLowerCase() === 'invgate' || editSecret.secretKey?.toLowerCase().startsWith('invgate.')) && (
                            <div className="integration-hint" style={{marginTop: '16px', padding: '12px', backgroundColor: '#e7f3ff', borderRadius: '4px', border: '1px solid #b3d7ff'}}>
                                <strong>InvGate Integration:</strong>
                                <ul style={{marginTop: '8px', marginBottom: 0, paddingLeft: '20px'}}>
                                    <li>Username: InvGate account username</li>
                                    <li>API Key: InvGate API authentication key (x-api-key header)</li>
                                    <li>Base URL: InvGate API endpoint (e.g., https://yourcompany.sd.cloud.invgate.net/api/v1/)</li>
                                </ul>
                            </div>
                        )}
                    </div>
                )}

                {secretType === 'MySqlConnection' && (
                    <div className="form-section">
                        <h3>MySQL Database Connection</h3>
                        <div className="form-row">
                            <div className="form-field">
                                <label>Server</label>
                                <input
                                    type="text"
                                    value={editSecret.mySqlServer || ''}
                                    onChange={e => handleFieldChange('mySqlServer', e.target.value)}
                                    placeholder="localhost or IP address"
                                />
                            </div>
                            <div className="form-field">
                                <label>Port</label>
                                <input
                                    type="number"
                                    value={editSecret.mySqlPort || 3306}
                                    onChange={e => handleFieldChange('mySqlPort', parseInt(e.target.value, 10) || 3306)}
                                />
                            </div>
                        </div>
                        <div className="form-field">
                            <label>Database</label>
                            <input
                                type="text"
                                value={editSecret.mySqlDatabase || ''}
                                onChange={e => handleFieldChange('mySqlDatabase', e.target.value)}
                            />
                        </div>
                        <div className="form-field">
                            <label>Additional Options</label>
                            <input
                                type="text"
                                value={editSecret.mySqlOptions || ''}
                                onChange={e => handleFieldChange('mySqlOptions', e.target.value)}
                                placeholder="e.g., Charset=utf8mb4;SslMode=Required"
                            />
                        </div>
                    </div>
                )}

                {secretType === 'MsSqlConnection' && (
                    <div className="form-section">
                        <h3>MS SQL Server Connection</h3>
                        <div className="form-row">
                            <div className="form-field">
                                <label>Server</label>
                                <input
                                    type="text"
                                    value={editSecret.msSqlServer || ''}
                                    onChange={e => handleFieldChange('msSqlServer', e.target.value)}
                                    placeholder="SERVER\\INSTANCE or IP,PORT"
                                />
                            </div>
                            <div className="form-field">
                                <label>Database</label>
                                <input
                                    type="text"
                                    value={editSecret.msSqlDatabase || ''}
                                    onChange={e => handleFieldChange('msSqlDatabase', e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="form-row checkboxes">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={editSecret.msSqlUseWindowsAuth || false}
                                    onChange={e => handleFieldChange('msSqlUseWindowsAuth', e.target.checked)}
                                />
                                Use Windows Authentication
                            </label>
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={editSecret.msSqlEncrypt || false}
                                    onChange={e => handleFieldChange('msSqlEncrypt', e.target.checked)}
                                />
                                Encrypt Connection
                            </label>
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={editSecret.msSqlTrustServerCertificate || false}
                                    onChange={e => handleFieldChange('msSqlTrustServerCertificate', e.target.checked)}
                                />
                                Trust Server Certificate
                            </label>
                        </div>
                        <div className="form-row">
                            <div className="form-field">
                                <label>Connection Timeout (sec)</label>
                                <input
                                    type="number"
                                    value={editSecret.msSqlConnectionTimeout || 30}
                                    onChange={e => handleFieldChange('msSqlConnectionTimeout', parseInt(e.target.value, 10) || 30)}
                                />
                            </div>
                            <div className="form-field">
                                <label>Command Timeout (sec)</label>
                                <input
                                    type="number"
                                    value={editSecret.msSqlCommandTimeout || 30}
                                    onChange={e => handleFieldChange('msSqlCommandTimeout', parseInt(e.target.value, 10) || 30)}
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-field">
                                <label>Max Pool Size</label>
                                <input
                                    type="number"
                                    value={editSecret.msSqlMaxPoolSize || 100}
                                    onChange={e => handleFieldChange('msSqlMaxPoolSize', parseInt(e.target.value, 10) || 100)}
                                />
                            </div>
                            <div className="form-field">
                                <label>Min Pool Size</label>
                                <input
                                    type="number"
                                    value={editSecret.msSqlMinPoolSize || 5}
                                    onChange={e => handleFieldChange('msSqlMinPoolSize', parseInt(e.target.value, 10) || 5)}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {secretType === 'AzureAd' && (
                    <div className="form-section">
                        <h3>Azure AD Configuration</h3>
                        <div className="form-row">
                            <div className="form-field">
                                <label>Tenant ID</label>
                                <input
                                    type="text"
                                    value={editSecret.azureTenantId || ''}
                                    onChange={e => handleFieldChange('azureTenantId', e.target.value)}
                                />
                            </div>
                            <div className="form-field">
                                <label>Client ID (Application ID)</label>
                                <input
                                    type="text"
                                    value={editSecret.azureClientId || ''}
                                    onChange={e => handleFieldChange('azureClientId', e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="form-field password-field">
                            <label>Client Secret</label>
                            <div className="password-input-group">
                                <input
                                    type={visiblePasswords['azureClientSecret'] ? 'text' : 'password'}
                                    value={editSecret.azureClientSecret || ''}
                                    onChange={e => handleFieldChange('azureClientSecret', e.target.value)}
                                />
                                <button
                                    type="button"
                                    className="toggle-password"
                                    onClick={() => togglePasswordVisibility('azureClientSecret')}
                                >
                                    {visiblePasswords['azureClientSecret'] ? <IoEyeOff /> : <IoEye />}
                                </button>
                            </div>
                        </div>
                        <div className="form-field">
                            <label>Authority URL</label>
                            <input
                                type="text"
                                value={editSecret.azureAuthority || ''}
                                onChange={e => handleFieldChange('azureAuthority', e.target.value)}
                                placeholder="https://login.microsoftonline.com/{tenant-id}"
                            />
                        </div>
                        <div className="form-field">
                            <label>Scopes (comma-separated)</label>
                            <input
                                type="text"
                                value={editSecret.azureScopes || ''}
                                onChange={e => handleFieldChange('azureScopes', e.target.value)}
                                placeholder="User.Read, Mail.Send"
                            />
                        </div>
                        {/* Additional configuration for Exchange/Graph API integrations */}
                        {(secretKey.includes('exchange') || secretKey.includes('microsoft365') || secretKey.includes('graph')) && (
                            <>
                                <h4 style={{marginTop: '20px', marginBottom: '12px', color: '#555'}}>Exchange/Graph API Settings</h4>
                                <div className="form-row">
                                    <div className="form-field">
                                        <label>Internal Domains (comma-separated)</label>
                                        <input
                                            type="text"
                                            value={editSecret.domains || ''}
                                            onChange={e => handleFieldChange('domains', e.target.value)}
                                            placeholder="example.com, other.com"
                                        />
                                        <small className="field-help">Internal email domains for Exchange integration</small>
                                    </div>
                                    <div className="form-field">
                                        <label>Max Parallelism</label>
                                        <input
                                            type="number"
                                            value={editSecret.maxParallelism || ''}
                                            onChange={e => handleFieldChange('maxParallelism', e.target.value ? parseInt(e.target.value, 10) : null)}
                                            placeholder="8"
                                        />
                                        <small className="field-help">Maximum concurrent API requests</small>
                                    </div>
                                </div>
                                <div className="form-field">
                                    <label>Page Size</label>
                                    <input
                                        type="number"
                                        value={editSecret.pageSize || ''}
                                        onChange={e => handleFieldChange('pageSize', e.target.value ? parseInt(e.target.value, 10) : null)}
                                        placeholder="100"
                                    />
                                    <small className="field-help">Items per page for paginated requests</small>
                                </div>
                                <div className="integration-hint" style={{marginTop: '16px', padding: '12px', backgroundColor: '#e7f3ff', borderRadius: '4px', border: '1px solid #b3d7ff'}}>
                                    <strong>Exchange/Microsoft 365 Integration:</strong>
                                    <ul style={{marginTop: '8px', marginBottom: 0, paddingLeft: '20px'}}>
                                        <li>Tenant ID: Azure AD tenant identifier</li>
                                        <li>Client ID: App registration application ID</li>
                                        <li>Client Secret: App registration secret value</li>
                                        <li>Domains: Internal email domains (e.g., yourcompany.com)</li>
                                        <li>Scopes: Required Graph API permissions (e.g., Mail.Read, Calendars.Read)</li>
                                    </ul>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {secretType === 'OAuth' && (
                    <div className="form-section">
                        <h3>OAuth Configuration</h3>
                        <div className="form-row">
                            <div className="form-field">
                                <label>Client ID</label>
                                <input
                                    type="text"
                                    value={editSecret.oAuthClientId || ''}
                                    onChange={e => handleFieldChange('oAuthClientId', e.target.value)}
                                />
                            </div>
                            <div className="form-field password-field">
                                <label>Client Secret</label>
                                <div className="password-input-group">
                                    <input
                                        type={visiblePasswords['oAuthClientSecret'] ? 'text' : 'password'}
                                        value={editSecret.oAuthClientSecret || ''}
                                        onChange={e => handleFieldChange('oAuthClientSecret', e.target.value)}
                                    />
                                    <button
                                        type="button"
                                        className="toggle-password"
                                        onClick={() => togglePasswordVisibility('oAuthClientSecret')}
                                    >
                                        {visiblePasswords['oAuthClientSecret'] ? <IoEyeOff /> : <IoEye />}
                                    </button>
                                </div>
                            </div>
                        </div>
                        {/* Add Base URL and Secondary URL for integrations like Rubrik */}
                        <div className="form-row" style={{marginTop: '16px'}}>
                            <div className="form-field">
                                <label>Base URL</label>
                                <input
                                    type="text"
                                    value={editSecret.baseUrl || ''}
                                    onChange={e => handleFieldChange('baseUrl', e.target.value)}
                                    placeholder={editSecret.secretKey?.toLowerCase() === 'rubrik' ? 'https://rubrik.example.com' : 'https://api.example.com'}
                                    style={{
                                        borderColor: editSecret.baseUrl && !isValidUrl(editSecret.baseUrl) ? 'red' : undefined
                                    }}
                                />
                                <small className="field-help">
                                    {editSecret.secretKey?.toLowerCase() === 'rubrik' ? 'Rubrik API base URL' : 'API base URL (must start with http:// or https://)'}
                                </small>
                                {editSecret.baseUrl && !isValidUrl(editSecret.baseUrl) && (
                                    <small style={{ color: 'red', display: 'block', marginTop: '4px' }}>
                                        Invalid URL format. Must start with http:// or https://
                                    </small>
                                )}
                            </div>
                            <div className="form-field">
                                <label>Secondary URL {editSecret.secretKey?.toLowerCase() === 'rubrik' && '(Access Token URI)'}</label>
                                <input
                                    type="text"
                                    value={editSecret.secondaryUrl || ''}
                                    onChange={e => handleFieldChange('secondaryUrl', e.target.value)}
                                    placeholder={editSecret.secretKey?.toLowerCase() === 'rubrik' ? 'https://rubrik.example.com/api/client_token' : 'https://api.example.com/token'}
                                    style={{
                                        borderColor: editSecret.secondaryUrl && !isValidUrl(editSecret.secondaryUrl) ? 'red' : undefined
                                    }}
                                />
                                <small className="field-help">
                                    {editSecret.secretKey?.toLowerCase() === 'rubrik' ? 'Token endpoint URL' : 'Token URL or secondary endpoint (must start with http:// or https://)'}
                                </small>
                                {editSecret.secondaryUrl && !isValidUrl(editSecret.secondaryUrl) && (
                                    <small style={{ color: 'red', display: 'block', marginTop: '4px' }}>
                                        Invalid URL format. Must start with http:// or https://
                                    </small>
                                )}
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-field">
                                <label>Authorization URL</label>
                                <input
                                    type="text"
                                    value={editSecret.oAuthAuthUrl || ''}
                                    onChange={e => handleFieldChange('oAuthAuthUrl', e.target.value)}
                                />
                                <small className="field-help">Optional: OAuth authorization endpoint</small>
                            </div>
                            <div className="form-field">
                                <label>Token URL</label>
                                <input
                                    type="text"
                                    value={editSecret.oAuthTokenUrl || ''}
                                    onChange={e => handleFieldChange('oAuthTokenUrl', e.target.value)}
                                />
                                <small className="field-help">Optional: OAuth token endpoint (use Secondary URL for most integrations)</small>
                            </div>
                        </div>
                        <div className="form-field">
                            <label>Scopes (comma-separated)</label>
                            <input
                                type="text"
                                value={editSecret.oAuthScopes || ''}
                                onChange={e => handleFieldChange('oAuthScopes', e.target.value)}
                            />
                        </div>
                        <div className="form-field password-field">
                            <label>Refresh Token</label>
                            <div className="password-input-group">
                                <textarea
                                    value={editSecret.oAuthRefreshToken || ''}
                                    onChange={e => handleFieldChange('oAuthRefreshToken', e.target.value)}
                                    rows={3}
                                    style={{ fontFamily: visiblePasswords['oAuthRefreshToken'] ? 'inherit' : 'monospace' }}
                                />
                                <button
                                    type="button"
                                    className="toggle-password"
                                    onClick={() => togglePasswordVisibility('oAuthRefreshToken')}
                                >
                                    {visiblePasswords['oAuthRefreshToken'] ? <IoEyeOff /> : <IoEye />}
                                </button>
                            </div>
                            <small className="field-help">Optional: for refresh token flow</small>
                        </div>
                        {/* Rubrik-specific hint */}
                        {editSecret.secretKey?.toLowerCase() === 'rubrik' && (
                            <div className="integration-hint" style={{marginTop: '16px', padding: '12px', backgroundColor: '#e7f3ff', borderRadius: '4px', border: '1px solid #b3d7ff'}}>
                                <strong>Rubrik Integration:</strong>
                                <ul style={{marginTop: '8px', marginBottom: 0, paddingLeft: '20px'}}>
                                    <li>Client ID: OAuth client ID from Rubrik</li>
                                    <li>Client Secret: OAuth client secret from Rubrik</li>
                                    <li>Base URL: Rubrik server URL (e.g., https://rubrik.yourdomain.com)</li>
                                    <li>Secondary URL (Access Token URI): Token endpoint (e.g., https://rubrik.yourdomain.com/api/client_token)</li>
                                </ul>
                            </div>
                        )}
                    </div>
                )}

                {secretType === 'JwtKey' && (
                    <div className="form-section">
                        <h3>JWT Signing Key</h3>
                        <div className="form-field password-field full-width">
                            <label>Signing Key</label>
                            <div className="password-input-group">
                                <textarea
                                    value={editSecret.password || ''}
                                    onChange={e => handleFieldChange('password', e.target.value)}
                                    rows={3}
                                    placeholder="Strong random key for JWT signing"
                                    style={{ fontFamily: visiblePasswords['password'] ? 'inherit' : 'monospace' }}
                                />
                                <button
                                    type="button"
                                    className="toggle-password"
                                    onClick={() => togglePasswordVisibility('password')}
                                >
                                    {visiblePasswords['password'] ? <IoEyeOff /> : <IoEye />}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {secretType === 'EmailConfig' && (
                    <div className="form-section">
                        <h3>Email Configuration</h3>
                        <div className="form-field">
                            <label>Email Provider</label>
                            <select
                                value={editSecret.emailProvider || 'SMTP'}
                                onChange={e => handleFieldChange('emailProvider', e.target.value)}
                            >
                                <option value="SMTP">SMTP (Basic Auth - Legacy)</option>
                                <option value="SMTPOAuth2">SMTP OAuth2 (Office 365 Modern Auth)</option>
                                <option value="Microsoft365">Microsoft 365 (Graph API)</option>
                            </select>
                            <small className="field-help">Select the email service provider to use. Use SMTP OAuth2 for Office 365 with modern authentication.</small>
                        </div>

                        {/* Common email settings */}
                        <div className="form-row">
                            <div className="form-field">
                                <label>From Email Address *</label>
                                <input
                                    type="email"
                                    value={editSecret.emailFromAddress || ''}
                                    onChange={e => handleFieldChange('emailFromAddress', e.target.value)}
                                    placeholder="noreply@yourdomain.com"
                                />
                                <small className="field-help">Email address used as the sender</small>
                            </div>
                            <div className="form-field">
                                <label>From Display Name</label>
                                <input
                                    type="text"
                                    value={editSecret.emailFromName || ''}
                                    onChange={e => handleFieldChange('emailFromName', e.target.value)}
                                    placeholder="CapTrac Notifications"
                                />
                            </div>
                        </div>

                        {/* SMTP Basic Auth settings */}
                        {(editSecret.emailProvider === 'SMTP' || !editSecret.emailProvider) && (
                            <>
                                <h4 style={{marginTop: '20px', marginBottom: '12px', color: '#555'}}>SMTP Settings (Basic Authentication)</h4>
                                <div className="form-row">
                                    <div className="form-field">
                                        <label>SMTP Server *</label>
                                        <input
                                            type="text"
                                            value={editSecret.smtpServer || ''}
                                            onChange={e => handleFieldChange('smtpServer', e.target.value)}
                                            placeholder="smtp.office365.com or mail.yourdomain.com"
                                        />
                                    </div>
                                    <div className="form-field">
                                        <label>Port</label>
                                        <input
                                            type="number"
                                            value={editSecret.smtpPort || 587}
                                            onChange={e => handleFieldChange('smtpPort', parseInt(e.target.value, 10) || 587)}
                                        />
                                        <small className="field-help">Common: 587 (TLS), 465 (SSL), 25 (unencrypted)</small>
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-field">
                                        <label>Username *</label>
                                        <input
                                            type="text"
                                            value={editSecret.username || ''}
                                            onChange={e => handleFieldChange('username', e.target.value)}
                                            placeholder="user@yourdomain.com"
                                        />
                                    </div>
                                    <div className="form-field password-field">
                                        <label>Password *</label>
                                        <div className="password-input-group">
                                            <input
                                                type={visiblePasswords['password'] ? 'text' : 'password'}
                                                value={editSecret.password || ''}
                                                onChange={e => handleFieldChange('password', e.target.value)}
                                            />
                                            <button
                                                type="button"
                                                className="toggle-password"
                                                onClick={() => togglePasswordVisibility('password')}
                                            >
                                                {visiblePasswords['password'] ? <IoEyeOff /> : <IoEye />}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className="form-row">
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={editSecret.smtpUseSsl !== false}
                                            onChange={e => handleFieldChange('smtpUseSsl', e.target.checked)}
                                        />
                                        Use SSL/TLS
                                    </label>
                                </div>
                                <div className="form-field full-width">
                                    <small className="field-help" style={{color: '#e67700', display: 'block', marginTop: '8px'}}>
                                        <strong>⚠️ Note:</strong> Basic authentication is deprecated for Office 365. If you&apos;re using Office 365, use &quot;SMTP OAuth2&quot; or &quot;Microsoft 365 (Graph API)&quot; instead.
                                    </small>
                                </div>
                            </>
                        )}

                        {/* SMTP OAuth2 settings (Office 365 Modern Auth) */}
                        {editSecret.emailProvider === 'SMTPOAuth2' && (
                            <>
                                <h4 style={{marginTop: '20px', marginBottom: '12px', color: '#555'}}>SMTP OAuth2 Settings (Office 365)</h4>
                                <div className="form-field full-width">
                                    <small className="field-help" style={{color: '#e67700', display: 'block', marginBottom: '16px', padding: '12px', backgroundColor: '#fff8e6', borderRadius: '4px', border: '1px solid #ffd699'}}>
                                        <strong>⚠️ Complex Setup Required:</strong> SMTP OAuth2 with client credentials requires additional Exchange Online configuration.
                                        <br/><strong>Recommendation:</strong> Use <strong>&quot;Microsoft 365 (Graph API)&quot;</strong> instead - it&apos;s much simpler to configure.
                                        <br/><br/>
                                        If you must use SMTP OAuth2:
                                        <br/>1. Azure AD: Add <strong>SMTP.SendAsApp</strong> application permission + grant admin consent
                                        <br/>2. Exchange Admin: Enable &quot;Authenticated SMTP&quot; on the mailbox
                                        <br/>3. PowerShell: Create Application Access Policy for the mailbox
                                    </small>
                                </div>
                                <div className="form-row">
                                    <div className="form-field">
                                        <label>SMTP Server *</label>
                                        <input
                                            type="text"
                                            value={editSecret.smtpServer || 'smtp.office365.com'}
                                            onChange={e => handleFieldChange('smtpServer', e.target.value)}
                                            placeholder="smtp.office365.com"
                                        />
                                    </div>
                                    <div className="form-field">
                                        <label>Port</label>
                                        <input
                                            type="number"
                                            value={editSecret.smtpPort || 587}
                                            onChange={e => handleFieldChange('smtpPort', parseInt(e.target.value, 10) || 587)}
                                        />
                                        <small className="field-help">Use 587 for STARTTLS (recommended)</small>
                                    </div>
                                </div>
                                <div className="form-row">
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={editSecret.smtpUseSsl !== false}
                                            onChange={e => handleFieldChange('smtpUseSsl', e.target.checked)}
                                        />
                                        Use SSL/TLS
                                    </label>
                                </div>
                                <h4 style={{marginTop: '20px', marginBottom: '12px', color: '#555'}}>Azure AD / Entra ID Settings</h4>
                                <div className="form-field">
                                    <label>Tenant ID *</label>
                                    <input
                                        type="text"
                                        value={editSecret.azureTenantId || ''}
                                        onChange={e => handleFieldChange('azureTenantId', e.target.value)}
                                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                    />
                                    <small className="field-help">Azure AD Tenant ID (Directory ID)</small>
                                </div>
                                <div className="form-row">
                                    <div className="form-field">
                                        <label>Client ID (Application ID) *</label>
                                        <input
                                            type="text"
                                            value={editSecret.azureClientId || ''}
                                            onChange={e => handleFieldChange('azureClientId', e.target.value)}
                                            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                        />
                                    </div>
                                    <div className="form-field password-field">
                                        <label>Client Secret *</label>
                                        <div className="password-input-group">
                                            <input
                                                type={visiblePasswords['azureClientSecret'] ? 'text' : 'password'}
                                                value={editSecret.azureClientSecret || ''}
                                                onChange={e => handleFieldChange('azureClientSecret', e.target.value)}
                                            />
                                            <button
                                                type="button"
                                                className="toggle-password"
                                                onClick={() => togglePasswordVisibility('azureClientSecret')}
                                            >
                                                {visiblePasswords['azureClientSecret'] ? <IoEyeOff /> : <IoEye />}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Microsoft 365 Graph API settings */}
                        {editSecret.emailProvider === 'Microsoft365' && (
                            <>
                                <h4 style={{marginTop: '20px', marginBottom: '12px', color: '#555'}}>Microsoft 365 / Azure AD Settings</h4>
                                <div className="form-field full-width">
                                    <small className="field-help" style={{color: '#0066cc', display: 'block', marginBottom: '16px', padding: '12px', backgroundColor: '#e7f3ff', borderRadius: '4px', border: '1px solid #b3d7ff'}}>
                                        <strong>📋 Azure AD Setup Required:</strong><br/>
                                        1. Go to <a href="https://portal.azure.com/#view/Microsoft_AAD_IAM/ActiveDirectoryMenuBlade/~/RegisteredApps" target="_blank" rel="noopener noreferrer">Azure Portal → App registrations</a><br/>
                                        2. Select your app → API permissions → Add permission<br/>
                                        3. Choose <strong>Microsoft Graph → Application permissions → Mail.Send</strong><br/>
                                        4. Click <strong>&quot;Grant admin consent for [Your Organization]&quot;</strong><br/>
                                        5. Wait 5-10 minutes for changes to propagate
                                    </small>
                                </div>
                                <div className="form-field">
                                    <label>Tenant ID *</label>
                                    <input
                                        type="text"
                                        value={editSecret.azureTenantId || ''}
                                        onChange={e => handleFieldChange('azureTenantId', e.target.value)}
                                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                    />
                                    <small className="field-help">Azure AD Tenant ID (Directory ID) - found in Azure AD Overview</small>
                                </div>
                                <div className="form-row">
                                    <div className="form-field">
                                        <label>Client ID (Application ID) *</label>
                                        <input
                                            type="text"
                                            value={editSecret.azureClientId || ''}
                                            onChange={e => handleFieldChange('azureClientId', e.target.value)}
                                            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                        />
                                        <small className="field-help">Found in App Registration → Overview</small>
                                    </div>
                                    <div className="form-field password-field">
                                        <label>Client Secret *</label>
                                        <div className="password-input-group">
                                            <input
                                                type={visiblePasswords['azureClientSecret'] ? 'text' : 'password'}
                                                value={editSecret.azureClientSecret || ''}
                                                onChange={e => handleFieldChange('azureClientSecret', e.target.value)}
                                            />
                                            <button
                                                type="button"
                                                className="toggle-password"
                                                onClick={() => togglePasswordVisibility('azureClientSecret')}
                                            >
                                                {visiblePasswords['azureClientSecret'] ? <IoEyeOff /> : <IoEye />}
                                            </button>
                                        </div>
                                        <small className="field-help">Use the secret &quot;Value&quot;, not the &quot;Secret ID&quot;</small>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {secretType === 'Configuration' && (
                    <div className="form-section">
                        <h3>Configuration Settings</h3>
                        <div className="form-row">
                            <div className="form-field">
                                <label>Base URL</label>
                                <input
                                    type="text"
                                    value={editSecret.baseUrl || ''}
                                    onChange={e => handleFieldChange('baseUrl', e.target.value)}
                                    placeholder="https://api.example.com"
                                    style={{
                                        borderColor: editSecret.baseUrl && !isValidUrl(editSecret.baseUrl) ? 'red' : undefined
                                    }}
                                />
                                <small className="field-help">
                                    Primary API or service endpoint URL (must start with http:// or https://)
                                </small>
                                {editSecret.baseUrl && !isValidUrl(editSecret.baseUrl) && (
                                    <small style={{ color: 'red', display: 'block', marginTop: '4px' }}>
                                        Invalid URL format. Must start with http:// or https://
                                    </small>
                                )}
                            </div>
                            <div className="form-field">
                                <label>Secondary URL</label>
                                <input
                                    type="text"
                                    value={editSecret.secondaryUrl || ''}
                                    onChange={e => handleFieldChange('secondaryUrl', e.target.value)}
                                    placeholder="https://api.example.com/token"
                                    style={{
                                        borderColor: editSecret.secondaryUrl && !isValidUrl(editSecret.secondaryUrl) ? 'red' : undefined
                                    }}
                                />
                                <small className="field-help">
                                    Token URL, SOAP URL, or other secondary endpoint (must start with http:// or https://)
                                </small>
                                {editSecret.secondaryUrl && !isValidUrl(editSecret.secondaryUrl) && (
                                    <small style={{ color: 'red', display: 'block', marginTop: '4px' }}>
                                        Invalid URL format. Must start with http:// or https://
                                    </small>
                                )}
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-field">
                                <label>Timeout (seconds)</label>
                                <input
                                    type="number"
                                    value={editSecret.timeoutSeconds || ''}
                                    onChange={e => handleFieldChange('timeoutSeconds', e.target.value ? parseInt(e.target.value, 10) : null)}
                                    placeholder="30"
                                />
                            </div>
                            <div className="form-field">
                                <label>Max Items</label>
                                <input
                                    type="number"
                                    value={editSecret.maxItems || ''}
                                    onChange={e => handleFieldChange('maxItems', e.target.value ? parseInt(e.target.value, 10) : null)}
                                    placeholder="1000"
                                />
                                <small className="field-help">Maximum items per request</small>
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-field">
                                <label>Max Parallelism</label>
                                <input
                                    type="number"
                                    value={editSecret.maxParallelism || ''}
                                    onChange={e => handleFieldChange('maxParallelism', e.target.value ? parseInt(e.target.value, 10) : null)}
                                    placeholder="8"
                                />
                            </div>
                            <div className="form-field">
                                <label>Page Size</label>
                                <input
                                    type="number"
                                    value={editSecret.pageSize || ''}
                                    onChange={e => handleFieldChange('pageSize', e.target.value ? parseInt(e.target.value, 10) : null)}
                                    placeholder="100"
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-field">
                                <label>Provider</label>
                                <input
                                    type="text"
                                    value={editSecret.provider || ''}
                                    onChange={e => handleFieldChange('provider', e.target.value)}
                                    placeholder="e.g., Gemini, Ollama"
                                />
                            </div>
                            <div className="form-field">
                                <label>Model Name</label>
                                <input
                                    type="text"
                                    value={editSecret.modelName || ''}
                                    onChange={e => handleFieldChange('modelName', e.target.value)}
                                    placeholder="e.g., gemini-2.5-flash"
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-field">
                                <label>Customer/Tenant ID</label>
                                <input
                                    type="text"
                                    value={editSecret.customerId || ''}
                                    onChange={e => handleFieldChange('customerId', e.target.value)}
                                    placeholder="Customer or tenant identifier"
                                />
                            </div>
                            <div className="form-field">
                                <label>Domains (comma-separated)</label>
                                <input
                                    type="text"
                                    value={editSecret.domains || ''}
                                    onChange={e => handleFieldChange('domains', e.target.value)}
                                    placeholder="example.com, other.com"
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-field">
                                <label>Max Characters</label>
                                <input
                                    type="number"
                                    value={editSecret.maxChars || ''}
                                    onChange={e => handleFieldChange('maxChars', e.target.value ? parseInt(e.target.value, 10) : null)}
                                    placeholder="20000"
                                />
                            </div>
                            <div className="form-field">
                                <label>Max Tokens</label>
                                <input
                                    type="number"
                                    value={editSecret.maxTokens || ''}
                                    onChange={e => handleFieldChange('maxTokens', e.target.value ? parseInt(e.target.value, 10) : null)}
                                    placeholder="4096"
                                />
                            </div>
                        </div>
                        <div className="form-row checkboxes">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={editSecret.isEnabled === true}
                                    onChange={e => handleFieldChange('isEnabled', e.target.checked)}
                                />
                                Feature Enabled
                            </label>
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={editSecret.allowInsecure === true}
                                    onChange={e => handleFieldChange('allowInsecure', e.target.checked)}
                                />
                                Allow Insecure Connections
                            </label>
                        </div>
                        <div className="form-field full-width">
                            <label>Custom JSON Data</label>
                            <textarea
                                value={editSecret.customData || ''}
                                onChange={e => handleFieldChange('customData', e.target.value)}
                                rows={4}
                                placeholder='{"additionalSetting": "value"}'
                            />
                            <small className="field-help">Additional settings in JSON format. Use structured fields above when possible for better validation.</small>
                        </div>
                        
                        {/* Integration-specific hints for Configuration type */}
                        {(editSecret.secretKey?.toLowerCase() === 'ollama' || editSecret.secretKey?.toLowerCase().includes('ollama')) && (
                            <div className="integration-hint" style={{marginTop: '16px', padding: '12px', backgroundColor: '#e7f3ff', borderRadius: '4px', border: '1px solid #b3d7ff'}}>
                                <strong>Ollama AI Configuration:</strong>
                                <ul style={{marginTop: '8px', marginBottom: 0, paddingLeft: '20px'}}>
                                    <li><strong>Base URL:</strong> Ollama server endpoint (e.g., http://172.16.16.100:11434)</li>
                                    <li><strong>Model Name:</strong> Ollama model to use (e.g., llama3.3:70b, tinyllama:1.1b)</li>
                                    <li><strong>Timeout Seconds:</strong> Request timeout (e.g., 30)</li>
                                </ul>
                            </div>
                        )}
                        {(editSecret.secretKey?.toLowerCase() === 'insights' || editSecret.secretKey?.toLowerCase().includes('insights')) && (
                            <div className="integration-hint" style={{marginTop: '16px', padding: '12px', backgroundColor: '#e7f3ff', borderRadius: '4px', border: '1px solid #b3d7ff'}}>
                                <strong>AI Insights Configuration:</strong>
                                <ul style={{marginTop: '8px', marginBottom: 0, paddingLeft: '20px'}}>
                                    <li><strong>Provider:</strong> AI provider to use (Gemini or Ollama)</li>
                                    <li><strong>Feature Enabled:</strong> Enable/disable insights feature</li>
                                    <li><strong>Max Chars:</strong> Maximum characters per group (e.g., 20000)</li>
                                    <li><strong>Max Tokens:</strong> Maximum output tokens (e.g., 327680)</li>
                                    <li><strong>Custom Data:</strong> Optional - {`{"MaxPromptChars": 163840, "MaxGroupChars": 20000}`}</li>
                                </ul>
                            </div>
                        )}
                        {(editSecret.secretKey?.toLowerCase() === 'exchange' || editSecret.secretKey?.toLowerCase().includes('exchange')) && (
                            <div className="integration-hint" style={{marginTop: '16px', padding: '12px', backgroundColor: '#e7f3ff', borderRadius: '4px', border: '1px solid #b3d7ff'}}>
                                <strong>Exchange Configuration:</strong>
                                <ul style={{marginTop: '8px', marginBottom: 0, paddingLeft: '20px'}}>
                                    <li><strong>Domains:</strong> Internal email domains (comma-separated, e.g., capland.co.za)</li>
                                    <li><strong>Max Parallelism:</strong> Maximum concurrent API requests (e.g., 8)</li>
                                    <li><strong>Page Size:</strong> Items per page for paginated requests (e.g., 999)</li>
                                    <li><strong>Note:</strong> Exchange requires Azure AD credentials configured separately in AzureAd secret</li>
                                </ul>
                            </div>
                        )}
                        {editSecret.secretKey?.toLowerCase().includes('jwt') && (
                            <div className="integration-hint" style={{marginTop: '16px', padding: '12px', backgroundColor: '#fff3cd', borderRadius: '4px', border: '1px solid #ffd699'}}>
                                <strong>⚠️ Legacy Configuration Format:</strong>
                                <p style={{marginTop: '8px', marginBottom: 0}}>
                                    This secret uses CustomData JSON format for configuration. While this still works, 
                                    consider migrating to use structured fields above for better validation and usability.
                                </p>
                                <div style={{marginTop: '12px'}}>
                                    <strong>JWT Config Format:</strong> {`{"Issuer": "capland.co.za", "Audience": "capland.co.za", "ExpiryInMinutes": 120}`}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {secretType === 'Custom' && (
                    <div className="form-section">
                        <h3>Custom Data (JSON)</h3>
                        <div className="form-field full-width">
                            <label>JSON Data</label>
                            <textarea
                                value={editSecret.customData || ''}
                                onChange={e => handleFieldChange('customData', e.target.value)}
                                rows={8}
                                placeholder='{"key": "value", "another": "field"}'
                            />
                        </div>
                    </div>
                )}
                    </>
                )}

                {/* Additional fields - shown for ALL secret types (both integration and traditional forms) */}
                <div className="form-section">
                    <h3>Additional Information</h3>
                    <div className="form-row">
                        <div className="form-field">
                            <label>Tags (comma-separated)</label>
                            <input
                                type="text"
                                value={editSecret.tags || ''}
                                onChange={e => handleFieldChange('tags', e.target.value)}
                                placeholder="production, critical, database"
                            />
                        </div>
                        <div className="form-field">
                            <label>Expiry Date</label>
                            <input
                                type="date"
                                value={editSecret.expiryDate ? editSecret.expiryDate.split('T')[0] : ''}
                                onChange={e => handleFieldChange('expiryDate', e.target.value ? new Date(e.target.value).toISOString() : null)}
                            />
                        </div>
                    </div>

                    {/* Expiry Reminder Settings - only show when expiry date is set */}
                    {editSecret.expiryDate && (
                        <div className="expiry-reminder-section" style={{marginTop: '16px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef'}}>
                            <h4 style={{marginTop: 0, marginBottom: '12px', color: '#495057'}}>
                                Expiry Reminder Settings
                            </h4>
                            <small className="field-help" style={{display: 'block', marginBottom: '12px', color: '#6c757d'}}>
                                Automatically create an action item before this secret expires to prevent silent expiry.
                            </small>
                            <div className="form-row" style={{alignItems: 'center'}}>
                                <label className="checkbox-label" style={{flex: '0 0 auto', marginRight: '20px'}}>
                                    <input
                                        type="checkbox"
                                        checked={editSecret.createExpiryReminder || false}
                                        onChange={e => handleFieldChange('createExpiryReminder', e.target.checked)}
                                    />
                                    Create Expiry Reminder
                                </label>
                            </div>
                            {editSecret.createExpiryReminder && (
                                <div className="form-row" style={{marginTop: '12px'}}>
                                    <div className="form-field">
                                        <label>Days Before Expiry</label>
                                        <input
                                            type="number"
                                            min="1"
                                            max="365"
                                            value={editSecret.expiryReminderDays || 30}
                                            onChange={e => handleFieldChange('expiryReminderDays', parseInt(e.target.value, 10) || 30)}
                                            placeholder="30"
                                        />
                                        <small className="field-help">Create action item this many days before expiry</small>
                                    </div>
                                    <div className="form-field">
                                        <label>Assign to Department</label>
                                        <select
                                            value={editSecret.expiryReminderDepartmentId || ''}
                                            onChange={e => handleFieldChange('expiryReminderDepartmentId', e.target.value ? parseInt(e.target.value, 10) : null)}
                                        >
                                            <option value="">Select department...</option>
                                            {/* Department API may return different property names depending on endpoint */}
                                            {/* departmentID/departmentName from /api/Department, id/name from alternatives */}
                                            {departments.map(dept => (
                                                <option key={dept.departmentID ?? dept.id} value={dept.departmentID ?? dept.id}>
                                                    {dept.departmentName ?? dept.name}
                                                </option>
                                            ))}
                                        </select>
                                        <small className="field-help">Department to assign the reminder action item</small>
                                    </div>
                                </div>
                            )}
                            {editSecret.expiryReminderCreated && (
                                <div style={{marginTop: '12px', padding: '8px 12px', backgroundColor: '#d4edda', borderRadius: '4px', color: '#155724'}}>
                                    ✓ Expiry reminder action item has been created
                                    {editSecret.expiryReminderActionItemId && (
                                        <span> (Action Item #{editSecret.expiryReminderActionItemId})</span>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="form-field full-width" style={{marginTop: '16px'}}>
                        <label>Notes</label>
                        <textarea
                            value={editSecret.notes || ''}
                            onChange={e => handleFieldChange('notes', e.target.value)}
                            rows={3}
                            placeholder="Additional instructions or information"
                        />
                    </div>
                    <div className="form-row">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={editSecret.isActive !== false}
                                onChange={e => handleFieldChange('isActive', e.target.checked)}
                            />
                            Active
                        </label>
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={editSecret.isMonitored || false}
                                onChange={e => handleFieldChange('isMonitored', e.target.checked)}
                            />
                            Monitor this secret
                        </label>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="secrets-page-new">
<div className="secrets-header">
                <h1>Application Secrets</h1>
                <p style={{ color: '#555', fontSize: '0.92rem', margin: '4px 0 0', lineHeight: 1.5 }}>
                    Store credentials, API keys, and connection strings used by integrations across CapTrac — including email mailboxes, Teams notifications, file storage, and router management.
                </p>
                <div className="header-actions">
                    <button className="btn-secondary" onClick={() => setShowExportModal(true)} title="Export secrets to JSON file">
                        <IoCloudDownload size={20} /> Export
                    </button>
                    <button className="btn-secondary" onClick={() => setShowImportModal(true)} title="Import secrets from JSON file">
                        <IoCloudUpload size={20} /> Import
                    </button>
                    <button className="btn-create" onClick={handleCreateClick}>
                        <IoAdd size={20} /> Create Secret
                    </button>
                </div>
            </div>

            {/* Database Backup Section */}
            <div className="secrets-category-card db-backup-panel" style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px' }}>
                    <div>
                        <h3 style={{ margin: 0 }}>🗄️ Database Backup (MySQL &amp; InfluxDB)</h3>
                        <p style={{ margin: '4px 0 0 0', fontSize: '13px' }}>
                            Target environment:{' '}
                            <strong style={{
                                color: dbEnvironment.toLowerCase() === 'production' ? '#f63040' : '#333',
                                textTransform: 'uppercase'
                            }}>
                                {dbEnvironment || 'Loading...'}
                            </strong>
                        </p>
                    </div>
                </div>

                <div style={{ padding: '0 16px 16px 16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    {/* MySQL */}
                    <div style={{ border: '1px solid #ddd', borderRadius: '6px', padding: '12px' }}>
                        <strong>MySQL Database</strong>
                        <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                            <button className="btn-db-export" onClick={() => handleDbExport('mysql')} disabled={exportingMySql}>
                                <IoCloudDownload size={16} /> {exportingMySql ? (dbStatus.mysql?.isReadyForDownload ? 'Preparing download...' : 'Exporting...') : 'Export'}
                            </button>
                            <button
                                className="btn-db-import"
                                onClick={() => !importingDb && setShowDbImportModal('mysql')}
                                disabled={importingDb}
                                title={importingDb ? 'An import is already in progress' : 'Import and overwrite MySQL database'}
                            >
                                <IoCloudUpload size={16} /> Import (Overwrite)
                            </button>
                        </div>
                        {dbStatus.mysql?.isRunning && dbStatus.mysql.operationType === 'Export' && (
                            <div className="db-progress-container">
                                <div className="db-progress-track"><div className="db-progress-bar" /></div>
                                <div className="db-progress-label">
                                    Exporting MySQL... {getDbElapsedSeconds('mysql')}s elapsed. You can navigate away — the download starts automatically once it's ready.
                                </div>
                            </div>
                        )}
                        {!dbStatus.mysql?.isRunning && dbStatus.mysql?.isReadyForDownload && (
                            <div className="db-progress-container">
                                <div className="db-progress-track"><div className="db-progress-bar" /></div>
                                <div className="db-progress-label">Export complete — starting download...</div>
                            </div>
                        )}
                        {dbStatus.mysql?.isRunning && dbStatus.mysql.operationType === 'Import' && (
                            <div className="db-progress-container">
                                <div className="db-progress-track"><div className="db-progress-bar" /></div>
                                <div className="db-progress-label">
                                    Overwriting MySQL... {getDbElapsedSeconds('mysql')}s elapsed. Do not navigate away.
                                </div>
                            </div>
                        )}
                    </div>

                    {/* InfluxDB */}
                    <div style={{ border: '1px solid #ddd', borderRadius: '6px', padding: '12px' }}>
                        <strong>InfluxDB</strong>
                        <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                            <button className="btn-db-export" onClick={() => handleDbExport('influx')} disabled={exportingInflux}>
                                <IoCloudDownload size={16} /> {exportingInflux ? (dbStatus.influx?.isReadyForDownload ? 'Preparing download...' : 'Exporting...') : 'Export'}
                            </button>
                            <button
                                className="btn-db-import"
                                onClick={() => !importingDb && setShowDbImportModal('influx')}
                                disabled={importingDb}
                                title={importingDb ? 'An import is already in progress' : 'Import and overwrite InfluxDB'}
                            >
                                <IoCloudUpload size={16} /> Import (Overwrite)
                            </button>
                        </div>
                        {dbStatus.influx?.isRunning && dbStatus.influx.operationType === 'Export' && (
                            <div className="db-progress-container">
                                <div className="db-progress-track"><div className="db-progress-bar" /></div>
                                <div className="db-progress-label">
                                    Exporting InfluxDB... {getDbElapsedSeconds('influx')}s elapsed. You can navigate away — the download starts automatically once it's ready.
                                </div>
                            </div>
                        )}
                        {!dbStatus.influx?.isRunning && dbStatus.influx?.isReadyForDownload && (
                            <div className="db-progress-container">
                                <div className="db-progress-track"><div className="db-progress-bar" /></div>
                                <div className="db-progress-label">Export complete — starting download...</div>
                            </div>
                        )}
                        {dbStatus.influx?.isRunning && dbStatus.influx.operationType === 'Import' && (
                            <div className="db-progress-container">
                                <div className="db-progress-track"><div className="db-progress-bar" /></div>
                                <div className="db-progress-label">
                                    Overwriting InfluxDB... {getDbElapsedSeconds('influx')}s elapsed.
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Database Import Confirmation Modal */}
            {showDbImportModal && (
                <div className="modal-overlay" onClick={() => setShowDbImportModal(null)}>
                    <div className="modal-content small" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2 style={{ color: '#f63040' }}>
                                <IoWarning size={24} /> Overwrite {showDbImportModal === 'mysql' ? 'MySQL' : 'InfluxDB'} — {dbEnvironment}
                            </h2>
                            <button className="close-btn" onClick={() => setShowDbImportModal(null)}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <div style={{ padding: '12px', backgroundColor: '#fdeaea', border: '1px solid #f63040', borderRadius: '4px', marginBottom: '16px' }}>
                                <strong style={{ color: '#f63040' }}>⚠️ This will PERMANENTLY and COMPLETELY overwrite</strong> the {showDbImportModal === 'mysql' ? 'MySQL' : 'InfluxDB'} database
                                for the <strong>{dbEnvironment}</strong> environment. This cannot be undone.
                            </div>

                            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>
                                Select {showDbImportModal === 'mysql' ? '.sql dump' : '.tar.gz backup'} file
                            </label>
                            <input
                                type="file"
                                // InfluxDB exports/imports now use InfluxDB's native `influx backup`/`influx
                                // restore` format (compressed TSM shard files packaged as .tar.gz) instead
                                // of an annotated CSV dump - smaller and faster to restore.
                                accept={showDbImportModal === 'mysql' ? '.sql' : '.gz,.tgz,.tar.gz'}
                                onChange={e => setDbImportFile(e.target.files[0])}
                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', marginBottom: '16px' }}
                            />

                            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>
                                Type <code>{dbEnvironment}</code> to confirm
                            </label>
                            <input
                                type="text"
                                value={dbImportConfirmText}
                                onChange={e => setDbImportConfirmText(e.target.value)}
                                placeholder={dbEnvironment}
                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                            />
                        </div>
                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={() => setShowDbImportModal(null)}>Cancel</button>
                            <button
                                className="btn-save"
                                style={{ backgroundColor: '#f63040', borderColor: '#f63040' }}
                                onClick={handleDbImport}
                                disabled={importingDb || !dbImportFile || dbImportConfirmText.trim().toUpperCase() !== dbEnvironment.trim().toUpperCase()}
                            >
                                <IoCloudUpload size={18} />
                                {importingDb ? 'Overwriting...' : 'Overwrite Database'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="loading">Loading secrets...</div>
            ) : (
                <div className="secrets-list">
                    {Object.keys(secretsByCategory).length === 0 ? (
                        <div className="empty-state">
                            <p>No secrets configured yet.</p>
                            <button className="btn-create" onClick={handleCreateClick}>
                                <IoAdd size={20} /> Create Your First Secret
                            </button>
                        </div>
                    ) : (
                        Object.entries(secretsByCategory).map(([category, secrets]) => (
                            <div key={category} className="category-group">
                                <div
                                    className="category-header"
                                    onClick={() => toggleCategory(category)}
                                >
                                    <div className="category-title">
                                        {expandedCategories[category] ? <IoChevronDown size={20} /> : <IoChevronForward size={20} />}
                                        <span>{category}</span>
                                        <span className="secret-count">{secrets.length}</span>
                                    </div>
                                </div>
                                {expandedCategories[category] && (
                                    <div className="secrets-grid">
                                        {secrets.map(secret => {
                                            // Check if this secret is managed by a dedicated settings page
                                            const managedRedirect = MANAGED_SECRET_REDIRECTS[secret.secretKey];
                                            if (managedRedirect) {
                                                return (
                                                    <div
                                                        key={secret.id}
                                                        className="secret-card managed-redirect"
                                                        onClick={() => onNavigateToTab?.(managedRedirect.tabId)}
                                                        title={`Managed in ${managedRedirect.label} — click to open`}
                                                        style={{ cursor: 'pointer', borderLeft: '4px solid #0d6efd' }}
                                                    >
                                                        <div className="secret-card-header">
                                                            <div className="secret-title">
                                                                <span style={{ marginRight: '6px' }}>{managedRedirect.icon}</span>
                                                                <strong>{secret.displayName || secret.secretKey}</strong>
                                                                <span className="badge" style={{ backgroundColor: '#0d6efd', color: '#fff', marginLeft: '8px', fontSize: '11px', padding: '2px 8px', borderRadius: '4px' }}
                                                                    title="This setting has a dedicated configuration page">
                                                                    Managed
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="secret-meta">
                                                            <p className="secret-description" style={{ color: '#495057', fontSize: '13px', margin: '8px 0' }}>
                                                                {managedRedirect.description}
                                                            </p>
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#0d6efd', fontSize: '13px', fontWeight: 500 }}>
                                                            <IoOpenOutline size={14} />
                                                            <span>Open File Storage Config</span>
                                                        </div>
                                                    </div>
                                                );
                                            }

                                            const incompleteCreds = hasIncompleteCreds(secret);
                                            return (
                                                <div
                                                    key={secret.id}
                                                    className={`secret-card ${incompleteCreds ? 'incomplete-creds' : ''}`}
                                                    onClick={() => handleSecretClick(secret.id)}
                                                    title={incompleteCreds ? 'Missing or incomplete credentials - click to configure' : ''}
                                                >
                                                    <div className="secret-card-header">
                                                        <div className="secret-title">
                                                            <strong>{secret.displayName || secret.secretKey}</strong>
                                                            {(() => {
                                                                const status = monitoringStatuses[secret.secretKey];
                                                                if (secret.isMonitored && status?.lastStatus === false) {
                                                                    return (
                                                                        <IoWarning
                                                                            size={13}
                                                                            color="#e67700"
                                                                            title="Monitored — last check failed"
                                                                            style={{ marginLeft: '6px', flexShrink: 0, verticalAlign: 'middle' }}
                                                                        />
                                                                    );
                                                                }
                                                                return (
                                                                    <span
                                                                        title={secret.isMonitored ? 'Monitored' : 'Not monitored'}
                                                                        style={{
                                                                            width: '9px', height: '9px', borderRadius: '50%',
                                                                            backgroundColor: secret.isMonitored ? '#28a745' : '#dc3545',
                                                                            display: 'inline-block', flexShrink: 0,
                                                                            verticalAlign: 'middle', marginLeft: '6px'
                                                                        }}
                                                                    />
                                                                );
                                                            })()}
                                                            {incompleteCreds && (
                                                                <span className="badge warning" title="Missing or incomplete credentials">
                                                                    ⚠️ Incomplete
                                                                </span>
                                                            )}
                                                            {!secret.isActive && <span className="badge inactive">Inactive</span>}
                                                            {secret.expiryDate && new Date(secret.expiryDate) < new Date() && (
                                                                <span className="badge expired">Expired</span>
                                                            )}
                                                            {(secret.secretKey === 'AdminCredentials' || secret.secretKey === 'Jwt.Key') && (
                                                                <span className="badge cicd-managed" title="Automatically updated by CI/CD pipeline on each deployment">
                                                                    🔄 CI/CD Managed
                                                                </span>
                                                            )}
                                                        </div>
                                                        <button
                                                            className="btn-delete"
                                                            onClick={(e) => handleDeleteClick(secret, e)}
                                                            title="Delete"
                                                        >
                                                            <IoTrash size={18} />
                                                        </button>
                                                    </div>
                                                    <div className="secret-meta">
                                                        <span className="secret-type">{secret.secretType}</span>
                                                        {secret.description && <p className="secret-description">{secret.description}</p>}
                                                    </div>
                                                    <div className="secret-indicators">
                                                        {secret.hasUsername && <span className="indicator">Username</span>}
                                                        {secret.hasPassword && <span className="indicator">Password</span>}
                                                        {secret.hasApiKey && <span className="indicator">API Key</span>}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* Edit/Create Modal */}
            {editSecret && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{isCreateMode ? 'Create Secret' : 'Edit Secret'}</h2>
                            <button className="close-btn" onClick={closeModal}>&times;</button>
                        </div>
                        <div className="modal-body">
                            {/* Missing Fields Banner */}
                            {!isCreateMode && (() => {
                                const missingFields = getMissingFields(editSecret);
                                if (missingFields.length === 0) return null;
                                return (
                                    <div className="missing-fields-banner">
                                        <div className="missing-fields-header">
                                            <IoWarning size={18} />
                                            <strong>Incomplete Configuration</strong>
                                        </div>
                                        <p>The following required fields are missing:</p>
                                        <ul>
                                            {missingFields.map((field, idx) => (
                                                <li key={idx}>{field}</li>
                                            ))}
                                        </ul>
                                    </div>
                                );
                            })()}
                            {renderFormFields()}
                            {/* Test Result Display */}
                            {testResult && (
                                <div className={`test-result ${testResult.success ? 'success' : 'error'}`}>
                                    <strong>{testResult.success ? '✓ Test Passed' : '✗ Test Failed'}</strong>
                                    <p>{testResult.message}</p>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={closeModal}>Cancel</button>
                            {canTestCredentials() && (
                                <button
                                    className="btn-test"
                                    onClick={handleTestCredentials}
                                    disabled={testing || saving}
                                    title="Test if these credentials work"
                                >
                                    <IoFlash size={18} />
                                    {testing ? 'Testing...' : 'Test Credentials'}
                                </button>
                            )}
                            <button
                                className="btn-save"
                                onClick={handleSave}
                                disabled={saving || !editSecret.secretKey}
                            >
                                <IoSave size={18} />
                                {saving ? 'Saving...' : 'Save Secret'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteTarget && (
                <div className="modal-overlay" onClick={() => setDeleteTarget(null)}>
                    <div className="modal-content small" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Delete Secret</h2>
                        </div>
                        <div className="modal-body">
                            <p>Are you sure you want to delete <strong>{deleteTarget.displayName || deleteTarget.secretKey}</strong>?</p>
                            <p className="warning">This action cannot be undone.</p>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={() => setDeleteTarget(null)}>Cancel</button>
                            <button className="btn-delete" onClick={handleDeleteConfirm}>
                                <IoTrash size={18} /> Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Export Modal */}
            {showExportModal && (
                <div className="modal-overlay" onClick={() => setShowExportModal(false)}>
                    <div className="modal-content small" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2><IoCloudDownload size={24} /> Export Secrets</h2>
                            <button className="close-btn" onClick={() => setShowExportModal(false)}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <p>Export all application secrets to a JSON file for backup or migration to another environment.</p>
                            <div style={{marginTop: '20px', marginBottom: '20px'}}>
                                <label className="checkbox-label" style={{display: 'flex', alignItems: 'center', padding: '12px', backgroundColor: '#fff3cd', borderRadius: '4px', border: '1px solid #ffd699'}}>
                                    <input
                                        type="checkbox"
                                        checked={exportIncludePasswords}
                                        onChange={e => setExportIncludePasswords(e.target.checked)}
                                        style={{marginRight: '12px'}}
                                    />
                                    <div>
                                        <strong>Include Passwords and Secrets</strong>
                                        <p style={{margin: '4px 0 0 0', fontSize: '13px', color: '#856404'}}>
                                            {exportIncludePasswords 
                                                ? '⚠️ Full export with credentials (keep secure!)' 
                                                : '🔒 Passwords will be redacted for security'}
                                        </p>
                                    </div>
                                </label>
                            </div>
                            <div style={{padding: '12px', backgroundColor: '#e7f3ff', borderRadius: '4px', border: '1px solid #b3d7ff', fontSize: '13px'}}>
                                <strong>📋 Note:</strong>
                                <ul style={{marginTop: '8px', marginBottom: 0, paddingLeft: '20px'}}>
                                    <li>CI/CD managed secrets (AdminCredentials, Jwt.Key) will be included</li>
                                    <li>Passwords can be redacted for configuration review</li>
                                    <li>Full exports should be stored securely (encrypted)</li>
                                </ul>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={() => setShowExportModal(false)}>Cancel</button>
                            <button 
                                className="btn-save" 
                                onClick={handleExport}
                                disabled={exportingSecrets}
                            >
                                <IoCloudDownload size={18} />
                                {exportingSecrets ? 'Exporting...' : 'Export Secrets'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Import Modal */}
            {showImportModal && (
                <div className="modal-overlay" onClick={() => setShowImportModal(false)}>
                    <div className="modal-content small" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2><IoCloudUpload size={24} /> Import Secrets</h2>
                            <button className="close-btn" onClick={() => setShowImportModal(false)}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <p>Import secrets from an exported JSON file. This is useful for migrating configurations between environments.</p>
                            <div style={{marginTop: '20px', marginBottom: '20px'}}>
                                <label style={{display: 'block', fontWeight: 'bold', marginBottom: '8px'}}>
                                    Select Export File
                                </label>
                                <input
                                    type="file"
                                    accept=".json"
                                    onChange={e => setImportFile(e.target.files[0])}
                                    style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
                                />
                                {importFile && (
                                    <small style={{display: 'block', marginTop: '8px', color: '#666'}}>
                                        Selected: {importFile.name}
                                    </small>
                                )}
                            </div>
                            <div style={{marginBottom: '20px'}}>
                                <label className="checkbox-label" style={{display: 'flex', alignItems: 'center', padding: '12px', backgroundColor: '#fff3cd', borderRadius: '4px', border: '1px solid #ffd699'}}>
                                    <input
                                        type="checkbox"
                                        checked={importOverwriteExisting}
                                        onChange={e => setImportOverwriteExisting(e.target.checked)}
                                        style={{marginRight: '12px'}}
                                    />
                                    <div>
                                        <strong>Overwrite Existing Secrets</strong>
                                        <p style={{margin: '4px 0 0 0', fontSize: '13px', color: '#856404'}}>
                                            {importOverwriteExisting 
                                                ? '⚠️ Existing secrets will be updated with imported values' 
                                                : '🔒 Only new secrets will be created (safe mode)'}
                                        </p>
                                    </div>
                                </label>
                            </div>
                            <div style={{padding: '12px', backgroundColor: '#e7f3ff', borderRadius: '4px', border: '1px solid #b3d7ff', fontSize: '13px'}}>
                                <strong>📋 Note:</strong>
                                <ul style={{marginTop: '8px', marginBottom: 0, paddingLeft: '20px'}}>
                                    <li>CI/CD managed secrets (AdminCredentials, Jwt.Key) will be automatically skipped</li>
                                    <li>Redacted passwords (***REDACTED***) will trigger warnings</li>
                                    <li>Import summary will show created, updated, and skipped secrets</li>
                                </ul>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={() => setShowImportModal(false)}>Cancel</button>
                            <button 
                                className="btn-save" 
                                onClick={handleImport}
                                disabled={importingSecrets || !importFile}
                            >
                                <IoCloudUpload size={18} />
                                {importingSecrets ? 'Importing...' : 'Import Secrets'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Integration Selector Modal */}
            {showIntegrationSelector && (
                <IntegrationSelector
                    onSelect={handleIntegrationSelect}
                    onCancel={handleSkipIntegrationSelection}
                />
            )}
        </div>
    );
};

export default SecretsPage;
